import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {Router} from '@angular/router';
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {NzUploadChangeParam, NzUploadComponent, NzUploadFile, NzUploadXHRArgs} from 'ng-zorro-antd/upload';
import {CommonModule, DatePipe, formatDate} from '@angular/common';
import {DragDropModule, CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {SavedHeaders} from '../property-list/property-list.models';
import {defaultSavedHeaders} from '../property-list/property-list.constants';
import {TemplateService} from '../services/template.service';
import { NzListModule } from 'ng-zorro-antd/list';
import {NzFormControlComponent, NzFormDirective, NzFormItemComponent} from 'ng-zorro-antd/form';
import {NzColDirective, NzRowDirective} from 'ng-zorro-antd/grid';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {NzInputDirective} from 'ng-zorro-antd/input';
import {NzOptionComponent, NzSelectComponent} from 'ng-zorro-antd/select';
import {NzButtonComponent} from 'ng-zorro-antd/button';
import {NzIconDirective} from 'ng-zorro-antd/icon';
import {NzTimePickerComponent, NzTimePickerModule} from 'ng-zorro-antd/time-picker';
import {NzMessageService} from 'ng-zorro-antd/message';
import {CompanyService} from '../services/company.service';
import {NzCheckboxModule} from 'ng-zorro-antd/checkbox';
import {ProgressService} from '../services/progress.service';
import {firstValueFrom, Subscription, timeout} from 'rxjs';
import {finalize} from 'rxjs/operators';
import {ADDRESS_PATTERN, BE_API_AUTH_URL, DOMAIN} from '../constants/app.constants';
import { isValidGermanPhoneNumber } from '../utils/validators/phone.validator';
import { normalizeWhatsAppNumber, whatsAppNumberValidator } from '../utils/whatsapp-number.validator';
import {NzColorPickerModule} from 'ng-zorro-antd/color-picker';
import {NzTabsModule} from 'ng-zorro-antd/tabs';
import {NzToolTipModule} from 'ng-zorro-antd/tooltip';
import {NzPopoverModule} from 'ng-zorro-antd/popover';
import {NzCollapseModule} from 'ng-zorro-antd/collapse';
import {AppMode, DEFAULT_APP_MODE} from '../models/app-mode.model';
import {
  TechnicalServiceCategoryKey,
  TechnicalServiceTypeOrder,
  TECHNICAL_SERVICE_CATEGORY_ORDER,
  cloneTechnicalServiceOrder,
  createEmptyTechnicalServiceOrder,
  flattenTechnicalServiceOrder,
  normalizeTechnicalServiceOrder
} from '../utils/technical-service-categories';
import {AuthService} from '../services/auth.service';
import { FormsModule } from '@angular/forms';
import {NzTagModule} from 'ng-zorro-antd/tag';
import {HospitalityLinkPreviewComponent} from '../hospitality-link-preview/hospitality-link-preview.component';
import {NzModalModule, NzModalService} from 'ng-zorro-antd/modal';
import {NzSwitchModule} from 'ng-zorro-antd/switch';

import {
  Company,
  CompanyQuickLink,
  HospitalityCatalogEntries,
  HospitalityLinkDocument,
  LandingProfile,
} from './company.models';
import {SavedQrPosterTemplate} from '../models/qr-poster-template.model';
import { CompanyHospitalityMixin } from './company.hospitality.mixin';
import {NzAlertModule} from 'ng-zorro-antd/alert';
import {Vibrant} from 'node-vibrant/browser';
import { shouldSuppressGenericError } from '../utils/http-error.utils';
import { CompanyThemeService } from '../services/company-theme.service';

const LOGO_FILE_TYPES = [
  { extension: '.jpg', mimeType: 'image/jpeg', displayName: 'JPG' },
  { extension: '.jpeg', mimeType: 'image/jpeg', displayName: 'JPEG' },
  { extension: '.png', mimeType: 'image/png', displayName: 'PNG' },
] as const;
const LOGO_ACCEPTED_EXTENSIONS: readonly string[] = LOGO_FILE_TYPES.map(({ extension }) => extension);
const LOGO_ACCEPTED_MIME_TYPES: readonly string[] = LOGO_FILE_TYPES.map(({ mimeType }) => mimeType);
const LOGO_SUPPORTED_FORMATS = LOGO_FILE_TYPES.map(({ displayName }) => displayName);
const LOGO_MAX_SIZE_MB = 2;
const LOGO_MAX_SIZE_BYTES = LOGO_MAX_SIZE_MB * 1024 * 1024;

interface LogoUploadErrorResponse {
  code?: string;
  maxSizeMb?: number;
}

const dayAbbreviations: { [key: string]: string } = {
  Monday: 'Mo',
  Tuesday: 'Di',
  Wednesday: 'Mi',
  Thursday: 'Do',
  Friday: 'Fr',
  Saturday: 'Sa',
  Sunday: 'So'
};
// Build an index: 'Mo' -> 0, 'Di' -> 1, ... using dayAbbreviations order
const dayOrderIndex: Record<string, number> = Object.keys(dayAbbreviations)
  .map(k => dayAbbreviations[k])
  .reduce((acc, abbr, i) => (acc[abbr] = i, acc), {} as Record<string, number>);

const serviceTypeTranslationKeys: { [key: string]: string } = {
  'Heating': 'HEATING',
  'Sanitary': 'SANITARY',
  'Smoke Detector': 'SMOKE_DETECTOR',
  'Elevator': 'ELEVATOR',
  'Winter Service': 'WINTER_SERVICE',
  'Electrical': 'ELECTRICAL',
  'Garage Door': 'GARAGE_DOOR',
  'Pipe Blockage': 'PIPE_BLOCKAGE',
  'Pipe Burst & Water Damage': 'PIPE_BURST_WATER_DAMAGE',
  'Cable Television': 'CABLE_TELEVISION',
  'Meter Reading Service': 'METER_READING_SERVICE',
  'Bulk Waste Collection': 'BULK_WASTE_COLLECTION',
  'Gas Supply/Emergency Service': 'GAS_SUPPLY_EMERGENCY_SERVICE',
  'Power Supply/Emergency Service': 'POWER_SUPPLY_EMERGENCY_SERVICE',
  'Water Supply': 'WATER_SUPPLY',
  'Police': 'POLICE',
  'Stairwell Cleaning': 'STAIRWELL_CLEANING',
  'Locksmith Service': 'LOCKSMITH_SERVICE',
  'Chimney Sweep': 'CHIMNEY_SWEEP',
  'Garden Maintenance': 'GARDEN_MAINTENANCE',
  'Pest Control': 'PEST_CONTROL',
  'Energy Supply': 'ENERGY_SUPPLY',
  'Waste Disposal': 'WASTE_DISPOSAL',
  'Building Insurance': 'BUILDING_INSURANCE',
  'Gas Emergency/Gas Odor': 'GAS_EMERGENCY_GAS_ODOR',
  'Recyclable Waste Disposal': 'RECYCLABLE_WASTE_DISPOSAL',
  'Heating Oil Supply': 'HEATING_OIL_SUPPLY',
  'District Heating Supplier': 'DISTRICT_HEATING_SUPPLIER',
  'Lifting Systems and Pumps': 'LIFTING_SYSTEMS_AND_PUMPS',
  'Electric Storage Heater': 'ELECTRIC_STORAGE_HEATER',
  'Locking System Maintenance': 'LOCKING_SYSTEM_MAINTENANCE',
  'Green Roof Maintenance': 'GREEN_ROOF_MAINTENANCE',
  'Rental Mat Service': 'RENTAL_MAT_SERVICE',
  'Street and Sidewalk Cleaning': 'STREET_AND_SIDEWALK_CLEANING',
  'Double Parker': 'DOUBLE_PARKER',
  'Gate/Gate Systems': 'GATE_GATE_SYSTEMS',
  'Sewer/Blockage': 'SEWER_BLOCKAGE',
  'Solar System/PVA': 'SOLAR_SYSTEM_PVA',
  'Ventilation': 'VENTILATION',
  'Apartment Transfer Station (DHW/Heating)': 'APARTMENT_TRANSFER_STATION_DHW_HEATING',
  'Smoke and Heat Extraction System': 'SMOKE_AND_HEAT_EXTRACTION_SYSTEM',
  'Fire Extinguisher': 'FIRE_EXTINGUISHER',
  'Fire Doors': 'FIRE_DOORS',
  'Fire Alarm System': 'FIRE_ALARM_SYSTEM',
  'Heating Cost Allocator': 'HEATING_COST_ALLOCATOR',
  'WE Meter': 'WE_METER',
  'Coating (UG)': 'COATING_UG',
  'Windows': 'WINDOWS',
  'Roller Shutter/Raffstore': 'ROLLER_SHUTTER_RAFFSTORE',
  'Doors': 'DOORS',
  'Roofer': 'ROOFER',
  'Property Developer': 'PROPERTY_DEVELOPER',
  'Green Pruning': 'GREEN_PRUNING',
};
@Component({
  selector: 'app-company',
  templateUrl: './company.component.html',
  styleUrls: ['./company.component.less'],
  standalone: true,
  imports: [
    NzListModule,
    NzFormItemComponent,
    NzFormDirective,
    ReactiveFormsModule,
    NzRowDirective,
    NzColDirective,
    TranslateModule,
    NzInputDirective,
    NzSelectComponent,
    NzOptionComponent,
    CommonModule,
    DragDropModule,
    NzUploadComponent,
    NzButtonComponent,
    NzIconDirective,
    NzTimePickerComponent,
    NzTimePickerModule,
    NzCheckboxModule,
    NzColorPickerModule,
    NzFormControlComponent,
    NzTabsModule,
    NzToolTipModule,
    NzPopoverModule,
    NzCollapseModule,
    FormsModule,
    NzTagModule,
    NzModalModule,
    HospitalityLinkPreviewComponent,
    NzAlertModule,
    NzSwitchModule
  ],
  providers: [DatePipe, NzMessageService],
})
export class CompanyComponent extends CompanyHospitalityMixin implements OnInit, OnDestroy {
  readonly landingProfileTemplateInheritValue = '__inherit_company_qr_poster_template__';
  defaultLogoUrl = `${DOMAIN}/assets/scanafy_company_logo_croped.png`;
  weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  emergencyWeekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  companyForm!: FormGroup;
  landingProfileForm!: FormGroup;
  uploadUrl =  `${BE_API_AUTH_URL}/upload-logo`; // Backend logo upload URL
  readonly logoAcceptedExtensions = LOGO_ACCEPTED_EXTENSIONS.join(',');
  logoUrl: string | undefined = ''; // Holds the URL of the uploaded logo
  logoPreviewState: 'loading' | 'ready' | 'empty' | 'error' = 'loading';
  paletteDetectionCompleted = false;
  selectedThemeColor = '#2A74D2'; // Default color
  detectedPalette: string[] = [];
  isDetectingPalette = false;
  // Prevent double-save / duplicate submits
  isSaving = false;

  recommendedPaletteColor: string | null = null;

  company: Company | null = null;
  landingProfiles: LandingProfile[] = [];
  landingProfilesLoading = false;
  landingProfileSaving = false;
  editingLandingProfileId: string | null = null;
  landingProfileModalVisible = false;
  landingProfileQrPosterTemplates: SavedQrPosterTemplate[] = [];
  landingProfileQrPosterTemplatesLoading = false;
  readonly profileLogoUploadData = { target: 'landing-profile' };
  useCustomProfileLogo = false;
  useCustomProfileThemeColor = false;
  profileLogoUrl = '';
  profileLogoPreviewState: 'loading' | 'ready' | 'empty' = 'empty';
  profileDetectedPalette: string[] = [];
  profilePaletteDetectionCompleted = false;
  isDetectingProfilePalette = false;
  profileThemeColorSelectionSource: 'palette' | 'custom' | null = null;
  profileCustomThemeColorControl = new FormControl<string | null>(null);
  private profileCustomColorPickerReady = false;
  private profileCustomColorPickerUserInteracted = false;
  formattedAvailability: any[] = [];
  formattedEmergencyAvailability: any[] = [];
  propertyManager: any;
  technicalAssetsManager: any;
  technicalServicePreviewGroups: Array<{
    key: TechnicalServiceCategoryKey;
    services: Array<{ type: string; name: string; phone: string }>;
  }> = [];
  orderedTechnicalServiceTypes: TechnicalServiceTypeOrder = createEmptyTechnicalServiceOrder();
  serviceCategories = TECHNICAL_SERVICE_CATEGORY_ORDER.map((key) => ({
    key,
    translationKey: `COMPANY_PAGE.TECHNICAL_SERVICE_CATEGORIES.${key}`
  }));
  connectedDropListIds: string[] = [];
  showAvailability: boolean = false;
  showEmergencyAvailability: boolean = false;
  private availabilityAutoEnabledNotified = false;
  private emergencyAutoEnabledNotified = false;
  showEmergencyNumber112: boolean = false;
  showEmergencyNumber110: boolean = false;
  showPoisonInfoCenterHotline: boolean = false;
  showVeterinaryEmergencyHotline: boolean = false;
  showTelephoneCounselingHotline: boolean = false;
  showChildrenYouthHotline: boolean = false;
  showWomenGirlsEmergencyHotline: boolean = false;
  formModified: boolean = false;
  isFormInitialized: boolean = false;
  highlightLogoUpload: boolean = false;
  showThemeColorRequiredError = false;
  themeColorSelectionSource: 'backend' | 'palette' | 'custom' | null = null;
  customThemeColorControl = new FormControl<string | null>(null);
  private customColorPickerReady = false;
  private customColorPickerUserInteracted = false;
  uploadedLogoKey: string = '';
  quickLinksPreview: CompanyQuickLink[] = [];
  private readonly HOSPITALITY_DOCUMENT_MAX_SIZE_MB = 2;
  private hospitalityLinkDocumentUploads = new Set<string>();
  @ViewChild('additionalInfosSection') private additionalInfosSection?: ElementRef<HTMLElement>;
  private formChangesSubscription!: Subscription;
  private langChangeSubscription?: Subscription;
  private appModeSubscription?: Subscription;
  appMode: AppMode = DEFAULT_APP_MODE;
  // Track tabs; Display tab is the 4th (0-based index = 3)
  activeTabIndex = 0;
  readonly DISPLAY_TAB_INDEX = 3;
  readonly MAX_CARETAKER_TEXT = 300;
  readonly MAX_PUBLIC_LANDING_PAGE_MESSAGE = 120;
  readonly MAX_TECH_SERVICE_TEXT = 300;
  readonly GENERAL_LIMITS = {
    companyName: 120,
    email: 254,
    phone: 30,
    whatsappNumber: 30,
    address: 240,
    website: 2048,
    emergencyNumber: 30,
    quickLinkLabel: 120,
    quickLinkUrl: 2048,
  } as const;
  readonly HOW_TO_REACH_US_MANAGER_TYPES = ['propertyManager', 'technicalAssetsManager', 'sevManager', 'accountant'];
  templateHeaders: SavedHeaders | null = null;
  readonly LANDING_PROFILE_LIMITS = {
    profileName: 120,
    name: 120,
    email: 254,
    phone: 30,
    address: 240,
    website: 2048,
    emergencyNumber: 30,
  } as const;
  private lastConfiguredAppMode?: AppMode;
  isLogoWide = false;
  private mondayBulkCopyOffered = new Set<string>();

  constructor(
    protected fb: FormBuilder,
    protected translate: TranslateService,
    private message: NzMessageService,
    private modal: NzModalService,
    private companyService: CompanyService,
    private authService: AuthService,
    private progressService: ProgressService,
    private templateService: TemplateService,
    private companyThemeService: CompanyThemeService,
    private router: Router,
    ) {
    super();
    this.connectedDropListIds = this.serviceCategories.map((category) =>
      this.getCategoryDropListId(category.key)
    );
  }

  get isHospitality(): boolean {
    return this.appMode === 'hospitality';
  }
  ngOnInit(): void {
    this.appMode = this.authService.getAppMode();
    this.companyForm = this.buildCompanyForm();
    this.landingProfileForm = this.buildLandingProfileForm();
    this.companyForm.get('companyName')?.disable({ emitEvent: false });
    this.configureModeSpecificControls(this.appMode);
    this.setupFormSubscriptions();
    this.loadTemplateHeaders();

    this.propertyManager = {
      name: 'Alice Johnson',
      email: 'alice.johnson@example.com',
      phone: '+123456789',
      officeHours: 'Mon-Fri, 9am - 5pm',
    };

    this.technicalAssetsManager = {
      name: 'Bob Smith',
      email: 'bob.smith@example.com',
      phone: '+987654321',
      officeHours: 'Mon-Fri, 8am - 4pm',
    };

    this.getCompanyDetails();
    this.subscribeToFormChanges();
    this.langChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.updateTechnicalServicePreview();
      this.computeHospitalityPreview();
    });

    this.appModeSubscription = this.authService.getAppModeChanges().subscribe((mode) => {
      this.appMode = mode;
      this.configureModeSpecificControls(mode);
    });
  }

  private buildCompanyForm(): FormGroup {
    return this.fb.group({
      companyName: ['', [Validators.required, Validators.maxLength(this.GENERAL_LIMITS.companyName)]],
      companyDisplayEmail: [
        '',
        [Validators.email, Validators.maxLength(this.GENERAL_LIMITS.email)],
      ],
      companyPhone: [
        '',
        [
          Validators.required,
          Validators.maxLength(this.GENERAL_LIMITS.phone),
          (control: AbstractControl) => this.phoneValidator(control),
        ],
      ],
      whatsappNumber: ['', [
        Validators.maxLength(this.GENERAL_LIMITS.whatsappNumber),
        whatsAppNumberValidator(),
      ]],
      address: [
        '',
        [
          Validators.required,
          Validators.maxLength(this.GENERAL_LIMITS.address),
          (control: AbstractControl) => this.addressValidator(control),
        ],
      ],
      website: [
        '',
        [
          Validators.maxLength(this.GENERAL_LIMITS.website),
          (control: AbstractControl) => this.urlValidator(control),
        ],
      ],
      caretakerText: ['', [Validators.required, Validators.maxLength(this.MAX_CARETAKER_TEXT)]],
      publicLandingPageMessage: ['', [Validators.maxLength(this.MAX_PUBLIC_LANDING_PAGE_MESSAGE)]],
      multiLandingProfilesEnabled: [false],
      showEmergencyAvailability: [false],
      showAvailability: [false],
      showPropertyManager: [true],
      showTechnicalAssetsManager: [true],
      showSevManager: [true],
      showAccountant: [true],
      showManagerInHowToReachUs: [false],
      howToReachUsManagerOrder: [[]],
      showEmergencyNumbers: [false],
      showEmergencyNumber112: [false],
      showEmergencyNumber110: [false],
      showPoisonInfoCenterHotline: [false],
      poisonInfoCenterHotlineNumber: ['', [Validators.maxLength(this.GENERAL_LIMITS.emergencyNumber)]],
      showVeterinaryEmergencyHotline: [false],
      veterinaryEmergencyHotlineNumber: ['', [Validators.maxLength(this.GENERAL_LIMITS.emergencyNumber)]],
      showTelephoneCounselingHotline: [false],
      showChildrenYouthHotline: [false],
      showWomenGirlsEmergencyHotline: [false],
      landingPageEmergencyServicePlacementEnabled: [false],
      technicalServiceText: ['', [Validators.required, Validators.maxLength(this.MAX_TECH_SERVICE_TEXT)]],
      emergencyNumber: ['', [Validators.maxLength(this.GENERAL_LIMITS.emergencyNumber)]],
      themeColor: ['', Validators.required],
      logoUrl: [''],
      quickLinks: this.fb.array([]),
      technicalServiceTypeOrder: [createEmptyTechnicalServiceOrder()],

      MondaySlots: this.fb.array([]),
      TuesdaySlots: this.fb.array([]),
      WednesdaySlots: this.fb.array([]),
      ThursdaySlots: this.fb.array([]),
      FridaySlots: this.fb.array([]),

      EmergencyMondaySlots: this.fb.array([]),
      EmergencyTuesdaySlots: this.fb.array([]),
      EmergencyWednesdaySlots: this.fb.array([]),
      EmergencyThursdaySlots: this.fb.array([]),
      EmergencyFridaySlots: this.fb.array([]),
      EmergencySaturdaySlots: this.fb.array([]),
      EmergencySundaySlots: this.fb.array([]),
    }, {
      validators: [
        this.validateAtLeastOneAvailability.bind(this),
        this.validateAtLeastOneEmergencyAvailability.bind(this),
        this.validateCompleteAvailability.bind(this),
        this.validateCompleteEmergencyAvailability.bind(this),
        this.howToReachUsManagerValidator.bind(this),
      ]
    });
  }

  private buildLandingProfileForm(): FormGroup {
    const landingProfileNameMinLength = 2;
    return this.fb.group({
      profileName: ['', [Validators.required, Validators.minLength(landingProfileNameMinLength), Validators.maxLength(this.LANDING_PROFILE_LIMITS.profileName)]],
      name: ['', [Validators.required, Validators.minLength(landingProfileNameMinLength), Validators.maxLength(this.LANDING_PROFILE_LIMITS.name)]],
      address: ['', [Validators.maxLength(this.LANDING_PROFILE_LIMITS.address), (control: AbstractControl) => this.addressValidator(control)]],
      phone: ['', [Validators.required, Validators.maxLength(this.LANDING_PROFILE_LIMITS.phone), (control: AbstractControl) => this.phoneValidator(control)]],
      email: ['', [Validators.email, Validators.maxLength(this.LANDING_PROFILE_LIMITS.email)]],
      website: ['', [Validators.maxLength(this.LANDING_PROFILE_LIMITS.website), (control: AbstractControl) => this.urlValidator(control)]],
      emergencyNumber: ['', [Validators.maxLength(this.LANDING_PROFILE_LIMITS.emergencyNumber), (control: AbstractControl) => this.phoneValidator(control)]],
      logoUrl: [''],
      themeColor: [''],
      qrPosterTemplateId: [this.landingProfileTemplateInheritValue],
      active: [true],
    });
  }

  private setupFormSubscriptions(): void {
    this.companyForm.get('showEmergencyAvailability')?.valueChanges.subscribe((val: boolean) => {
      this.showEmergencyAvailability = val;
      this.applyEmergencyNumberValidators(val);
    });

    this.applyEmergencyNumberValidators(this.companyForm.get('showEmergencyAvailability')?.value);

    this.companyForm.get('showEmergencyNumbers')?.valueChanges.subscribe((val: boolean) => {
      this.showEmergencyNumber112 = val;
      this.showEmergencyNumber110 = val;
      this.companyForm.patchValue(
        {
          showEmergencyNumber112: val,
          showEmergencyNumber110: val
        },
        { emitEvent: false }
      );
    });

    this.companyForm.get('showPoisonInfoCenterHotline')?.valueChanges.subscribe((val: boolean) => {
      this.showPoisonInfoCenterHotline = !!val;
      this.applyPoisonHotlineValidators(!!val);
    });

    this.applyPoisonHotlineValidators(!!this.companyForm.get('showPoisonInfoCenterHotline')?.value);

    this.companyForm.get('showVeterinaryEmergencyHotline')?.valueChanges.subscribe((val: boolean) => {
      this.showVeterinaryEmergencyHotline = !!val;
      this.applyVeterinaryEmergencyHotlineValidators(!!val);
    });

    this.applyVeterinaryEmergencyHotlineValidators(!!this.companyForm.get('showVeterinaryEmergencyHotline')?.value);

    this.companyForm.get('themeColor')?.valueChanges.subscribe((value) => {
      this.selectedThemeColor = value;
      this.setThemeColor(value);
    });

    this.companyForm.get('multiLandingProfilesEnabled')?.valueChanges.subscribe((enabled: boolean) => {
      if (enabled && !this.landingProfiles.length && this.company?._id) {
        this.loadLandingProfiles();
      }
      if (!enabled) {
        this.cancelLandingProfileEdit();
      }
    });
  }

  private configureModeSpecificControls(mode: AppMode): void {
    if (!this.companyForm) {
      return;
    }

    const previousMode = this.lastConfiguredAppMode;

    if (mode === 'hospitality') {
      this.ensureHospitalityLandingGroup(this.company?.hospitalityLanding);
      this.ensureHospitalityEntriesGroup({
        houseRules: this.company?.hospitalityHouseRules,
        checkIns: this.company?.hospitalityCheckIns,
        checkOuts: this.company?.hospitalityCheckOuts,
        applianceGuides: this.company?.hospitalityApplianceGuides,
        additionalInfos: this.company?.hospitalityAdditionalInfos,
      });
      this.toggleEmergencyControls(false);
      this.companyForm.get('hospitalityLanding')?.enable({ emitEvent: false });
      this.companyForm.get('hospitalityEntries')?.enable({ emitEvent: false });
      if (previousMode !== 'hospitality' && this.activeTabIndex === this.DISPLAY_TAB_INDEX) {
        this.activeTabIndex = 0;
      }
      this.computeHospitalityPreview();
    } else {
      this.toggleEmergencyControls(true);
      this.companyForm.get('hospitalityLanding')?.disable({ emitEvent: false });
      this.companyForm.get('hospitalityEntries')?.disable({ emitEvent: false });
      this.hospitalityPreview = { infoSections: [] };
    }

    this.lastConfiguredAppMode = mode;
  }

  private toggleEmergencyControls(enable: boolean): void {
    const controls = [
      'showEmergencyAvailability',
      'showEmergencyNumbers',
      'showEmergencyNumber112',
      'showEmergencyNumber110',
      'showPoisonInfoCenterHotline',
      'poisonInfoCenterHotlineNumber',
      'showVeterinaryEmergencyHotline',
      'veterinaryEmergencyHotlineNumber',
      'showTelephoneCounselingHotline',
      'showChildrenYouthHotline',
      'showWomenGirlsEmergencyHotline',
      'emergencyNumber'
    ];
    controls.forEach((key) => {
      const control = this.companyForm.get(key);
      if (!control) {
        return;
      }
      if (enable) {
        control.enable({ emitEvent: false });
      } else {
        if (key === 'showEmergencyAvailability' || key === 'showEmergencyNumbers') {
          control.setValue(false, { emitEvent: false });
        }
        if (key === 'emergencyNumber') {
          control.setValue('', { emitEvent: false });
        }
        control.disable({ emitEvent: false });
      }
    });

    if (!enable) {
      this.showEmergencyAvailability = false;
      this.showEmergencyNumber112 = false;
      this.showEmergencyNumber110 = false;
      this.showPoisonInfoCenterHotline = false;
      this.showVeterinaryEmergencyHotline = false;
      this.showTelephoneCounselingHotline = false;
      this.showChildrenYouthHotline = false;
      this.showWomenGirlsEmergencyHotline = false;
      this.applyEmergencyNumberValidators(false);
      this.applyPoisonHotlineValidators(false);
      this.applyVeterinaryEmergencyHotlineValidators(false);
    }

    this.emergencyWeekDays.forEach((day) => {
      const control = this.companyForm.get(`Emergency${day}Slots`);
      if (!control) {
        return;
      }
      if (enable) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    });

    if (enable) {
      this.applyEmergencyNumberValidators(this.companyForm.get('showEmergencyAvailability')?.value);
    }
  }

  ngOnDestroy(): void {
    // Clean up subscription when the component is destroyed
    if (this.formChangesSubscription) {
      this.formChangesSubscription.unsubscribe();
    }
    this.langChangeSubscription?.unsubscribe();
    this.appModeSubscription?.unsubscribe();
  }

  // Custom phone number validator
  phoneValidator(control: AbstractControl): { [key: string]: boolean } | null {
    if (control.value && !isValidGermanPhoneNumber(control.value)) {
      return { invalidPhone: true };
    }
    return null;
  }

  addressValidator(control: AbstractControl): { [key: string]: boolean } | null {
    if (control.value && !ADDRESS_PATTERN.test(control.value)) {
      return { invalidAddress: true };
    }
    return null;
  }

  // Custom URL validator
  urlValidator(control: AbstractControl): { [key: string]: boolean } | null {
    const urlPattern = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/; // Simple URL pattern
    if (control.value && !urlPattern.test(control.value)) {
      return { invalidUrl: true };
    }
    return null;
  }

  onShowAvailabilityChange(checked: boolean): void {
    this.showAvailability = checked;
  }

  get quickLinks(): FormArray<FormGroup> {
    return this.companyForm.get('quickLinks') as FormArray<FormGroup>;
  }

  private createQuickLinkGroup(label: string = '', url: string = ''): FormGroup {
    return this.fb.group({
      label: [
        label,
        [Validators.required, Validators.maxLength(this.GENERAL_LIMITS.quickLinkLabel)],
      ],
      url: [
        url,
        [
          Validators.required,
          Validators.maxLength(this.GENERAL_LIMITS.quickLinkUrl),
          (control: AbstractControl) => this.urlValidator(control),
        ],
      ]
    });
  }

  addQuickLink(suppressDirtyCheck: boolean = false): void {
    this.quickLinks.push(this.createQuickLinkGroup());
    this.updateQuickLinksPreview();
    if (!suppressDirtyCheck && this.isFormInitialized && !this.formModified) {
      this.checkIfFormModified();
    }
  }

  removeQuickLink(index: number): void {
    // Always allow removing
    this.quickLinks.removeAt(index);
    this.updateQuickLinksPreview();

    // Mark the form dirty so valueChanges/guards see a change
    this.companyForm.markAsDirty();
    this.companyForm.updateValueAndValidity({ onlySelf: false, emitEvent: true });

    // Ensure the “unsaved changes” info appears if not shown yet
    if (this.isFormInitialized && !this.formModified) {
      this.checkIfFormModified();
    }
  }

  private setQuickLinks(links?: CompanyQuickLink[]): void {
    // clear existing controls
    while (this.quickLinks.length) {
      this.quickLinks.removeAt(0);
    }

    // only add actual links that exist from BE
    if (links && links.length) {
      links.forEach(link => {
        this.quickLinks.push(this.createQuickLinkGroup(link.label, link.url));
      });
    }

    // NO default empty row here
    this.updateQuickLinksPreview();
  }

  private updateQuickLinksPreview(): void {
    this.quickLinksPreview = this.quickLinks.controls
      .map(control => control.value as CompanyQuickLink)
      .filter(link => link.label?.trim() && link.url?.trim());
  }

  get landingProfilesEnabled(): boolean {
    return !!this.companyForm?.get('multiLandingProfilesEnabled')?.value;
  }

  hasLandingProfileFieldError(controlName: string, errorKey?: string): boolean {
    const control = this.landingProfileForm.get(controlName);
    if (!control || !(control.dirty || control.touched)) {
      return false;
    }
    return errorKey ? control.hasError(errorKey) : control.invalid;
  }

  startCreateLandingProfile(): void {
    this.loadLandingProfileQrPosterTemplates();
    this.editingLandingProfileId = null;
    this.landingProfileModalVisible = true;
    this.landingProfileForm.reset({
      profileName: '',
      name: '',
      address: '',
      phone: '',
      email: '',
      website: '',
      emergencyNumber: '',
      logoUrl: '',
      themeColor: '',
      qrPosterTemplateId: this.landingProfileTemplateInheritValue,
      active: true,
    });
    this.resetLandingProfileBranding();
    this.landingProfileForm.markAsPristine();
  }

  editLandingProfile(profile: LandingProfile): void {
    this.loadLandingProfileQrPosterTemplates();
    this.editingLandingProfileId = profile._id || null;
    this.landingProfileModalVisible = true;
    this.landingProfileForm.reset({
      profileName: profile.profileName || '',
      name: profile.name || '',
      address: profile.address || '',
      phone: profile.phone || '',
      email: profile.email || '',
      website: profile.website || '',
      emergencyNumber: profile.emergencyNumber || '',
      logoUrl: profile.logoUrl || '',
      themeColor: profile.themeColor || '',
      qrPosterTemplateId: profile.qrPosterTemplateId || this.landingProfileTemplateInheritValue,
      active: profile.active !== false,
    });
    this.useCustomProfileLogo = !!profile.logoUrl;
    this.profileLogoUrl = profile.logoPreviewUrl || '';
    this.profileLogoPreviewState = this.profileLogoUrl ? 'ready' : 'empty';
    this.useCustomProfileThemeColor = !!profile.themeColor;
    this.profileThemeColorSelectionSource = profile.themeColor ? 'custom' : null;
    this.resetProfileCustomColorPickerState(profile.themeColor || null);
    if (this.profileLogoUrl) {
      this.detectProfilePaletteFromPreview();
    } else {
      this.profileDetectedPalette = [];
      this.profilePaletteDetectionCompleted = false;
    }
    this.landingProfileForm.markAsPristine();
  }

  cancelLandingProfileEdit(): void {
    this.editingLandingProfileId = null;
    this.landingProfileModalVisible = false;
    this.landingProfileForm.reset({
      profileName: '',
      name: '',
      address: '',
      phone: '',
      email: '',
      website: '',
      emergencyNumber: '',
      logoUrl: '',
      themeColor: '',
      qrPosterTemplateId: this.landingProfileTemplateInheritValue,
      active: true,
    });
    this.resetLandingProfileBranding();
  }

  loadLandingProfiles(): void {
    this.landingProfilesLoading = true;
    this.companyService.listLandingProfiles(true)
      .pipe(finalize(() => (this.landingProfilesLoading = false)))
      .subscribe({
        next: (profiles) => {
          this.landingProfiles = Array.isArray(profiles) ? profiles : [];
          if (!this.editingLandingProfileId && !this.landingProfileForm.dirty) {
            this.cancelLandingProfileEdit();
          }
        },
        error: () => {
          this.landingProfiles = [];
          this.message.error(this.translate.instant('COMPANY_PAGE.LANDING_PROFILES.LOAD_ERROR'));
        }
      });
  }

  private loadLandingProfileQrPosterTemplates(): void {
    this.landingProfileQrPosterTemplatesLoading = true;
    this.companyService.getQrPosterTemplateState()
      .pipe(finalize(() => (this.landingProfileQrPosterTemplatesLoading = false)))
      .subscribe({
        next: (state) => {
          this.landingProfileQrPosterTemplates = Array.isArray(state?.templates) ? state.templates : [];
        },
        error: () => {
          this.landingProfileQrPosterTemplates = [];
          this.message.error(this.translate.instant('COMPANY_PAGE.LANDING_PROFILES.TEMPLATE_LOAD_ERROR'));
        },
      });
  }

  openQrPosterTemplateEditor(): void {
    const navigate = () => {
      this.cancelLandingProfileEdit();
      this.router.navigate(['/qr-poster-template']);
    };

    if (!this.landingProfileForm.dirty) {
      navigate();
      return;
    }

    this.modal.confirm({
      nzTitle: this.translate.instant('COMPANY_PAGE.LANDING_PROFILES.TEMPLATE_EDITOR_UNSAVED_TITLE'),
      nzContent: this.translate.instant('COMPANY_PAGE.LANDING_PROFILES.TEMPLATE_EDITOR_UNSAVED_MESSAGE'),
      nzOkText: this.translate.instant('COMPANY_PAGE.LANDING_PROFILES.TEMPLATE_EDITOR_UNSAVED_OK'),
      nzCancelText: this.translate.instant('COMMON.CANCEL'),
      nzOkDanger: true,
      nzOnOk: navigate,
    });
  }

  private normalizeLandingProfileQrPosterTemplateId(value: unknown): string | null {
    const templateId = typeof value === 'string' ? value.trim() : '';
    return templateId && templateId !== this.landingProfileTemplateInheritValue ? templateId : null;
  }

  saveLandingProfile(): void {
    if (this.landingProfileForm.invalid) {
      this.landingProfileForm.markAllAsTouched();
      return;
    }

    const payload = {
      ...this.landingProfileForm.getRawValue(),
      email: this.landingProfileForm.get('email')?.value?.trim() || '',
      profileName: this.landingProfileForm.get('profileName')?.value?.trim() || '',
      name: this.landingProfileForm.get('name')?.value?.trim() || '',
      address: this.landingProfileForm.get('address')?.value?.trim() || '',
      phone: this.landingProfileForm.get('phone')?.value?.trim() || '',
      website: this.landingProfileForm.get('website')?.value?.trim() || '',
      emergencyNumber: this.landingProfileForm.get('emergencyNumber')?.value?.trim() || '',
      logoUrl: this.landingProfileForm.get('logoUrl')?.value?.trim() || '',
      themeColor: this.landingProfileForm.get('themeColor')?.value?.trim() || '',
      qrPosterTemplateId: this.normalizeLandingProfileQrPosterTemplateId(
        this.landingProfileForm.get('qrPosterTemplateId')?.value
      ),
    };

    this.landingProfileSaving = true;
    const request$ = this.editingLandingProfileId
      ? this.companyService.updateLandingProfile(this.editingLandingProfileId, payload)
      : this.companyService.createLandingProfile(payload);

    request$
      .pipe(finalize(() => (this.landingProfileSaving = false)))
      .subscribe({
        next: () => {
          this.message.success(this.translate.instant(
            this.editingLandingProfileId
              ? 'COMPANY_PAGE.LANDING_PROFILES.UPDATE_SUCCESS'
              : 'COMPANY_PAGE.LANDING_PROFILES.CREATE_SUCCESS'
          ));
          this.cancelLandingProfileEdit();
          this.loadLandingProfiles();
        },
        error: () => {
          this.message.error(this.translate.instant('COMPANY_PAGE.LANDING_PROFILES.SAVE_ERROR'));
        }
      });
  }

  deactivateLandingProfile(profile: LandingProfile): void {
    if (!profile?._id) {
      return;
    }

    this.companyService.deactivateLandingProfile(profile._id).subscribe({
      next: () => {
        this.message.success(this.translate.instant('COMPANY_PAGE.LANDING_PROFILES.DELETE_SUCCESS'));
        if (this.editingLandingProfileId === profile._id) {
          this.cancelLandingProfileEdit();
        }
        this.loadLandingProfiles();
      },
      error: () => {
        this.message.error(this.translate.instant('COMPANY_PAGE.LANDING_PROFILES.DELETE_ERROR'));
      }
    });
  }

  onProfileLogoOverrideChange(enabled: boolean): void {
    this.useCustomProfileLogo = enabled;
    if (!enabled) {
      this.landingProfileForm.get('logoUrl')?.setValue('');
      this.profileLogoUrl = '';
      this.profileLogoPreviewState = 'empty';
      this.profileDetectedPalette = [];
      this.profilePaletteDetectionCompleted = false;
    }
  }

  onProfileThemeColorOverrideChange(enabled: boolean): void {
    this.useCustomProfileThemeColor = enabled;
    if (!enabled) {
      this.landingProfileForm.get('themeColor')?.setValue('');
      this.profileThemeColorSelectionSource = null;
      this.resetProfileCustomColorPickerState(null);
    }
  }

  handleProfileLogoChange(info: NzUploadChangeParam): void {
    if (info.file.status === 'uploading') {
      this.profileLogoPreviewState = 'loading';
    } else if (info.file.status === 'done') {
      const response = info.file.response;
      this.useCustomProfileLogo = true;
      this.profileLogoUrl = response.logoUrl || '';
      this.profileLogoPreviewState = this.profileLogoUrl ? 'ready' : 'empty';
      this.profileDetectedPalette = [];
      this.profilePaletteDetectionCompleted = false;
      this.landingProfileForm.get('logoUrl')?.setValue(response.s3Key || '');

      const file = info.file?.originFileObj as File | undefined;
      if (file) {
        this.isDetectingProfilePalette = true;
        this.extractPaletteFromLogoFile(file)
          .then((palette) => {
            this.profileDetectedPalette = palette;
          })
          .catch(() => undefined)
          .finally(() => {
            this.isDetectingProfilePalette = false;
            this.profilePaletteDetectionCompleted = true;
          });
      }
    } else if (info.file.status === 'error') {
      this.profileLogoPreviewState = this.profileLogoUrl ? 'ready' : 'empty';
      this.showLogoUploadError(info.file?.error);
    }
  }

  onProfilePaletteColorSelect(color: string): void {
    this.useCustomProfileThemeColor = true;
    this.profileThemeColorSelectionSource = 'palette';
    this.landingProfileForm.get('themeColor')?.setValue(color);
    this.profileCustomThemeColorControl.setValue(color, { emitEvent: false });
  }

  getProfileCustomThemeColorPreview(): string {
    const pickerColor = this.extractColorPickerValue(this.profileCustomThemeColorControl.value);
    const formColor = this.extractColorPickerValue(this.landingProfileForm.get('themeColor')?.value);
    return pickerColor || formColor || '#ffffff';
  }

  onProfileCustomColorPickerUserIntent(): void {
    this.profileCustomColorPickerUserInteracted = true;
  }

  onProfileCustomColorPickerChange(event: unknown): void {
    if (!this.profileCustomColorPickerReady || !this.profileCustomColorPickerUserInteracted) {
      return;
    }

    const color = this.extractColorPickerValue(event)
      || this.extractColorPickerValue(this.profileCustomThemeColorControl.value);
    if (!color) {
      return;
    }

    this.useCustomProfileThemeColor = true;
    this.profileThemeColorSelectionSource = 'custom';
    this.landingProfileForm.get('themeColor')?.setValue(color);
  }

  private resetLandingProfileBranding(): void {
    this.useCustomProfileLogo = false;
    this.useCustomProfileThemeColor = false;
    this.profileLogoUrl = '';
    this.profileLogoPreviewState = 'empty';
    this.profileDetectedPalette = [];
    this.profilePaletteDetectionCompleted = false;
    this.isDetectingProfilePalette = false;
    this.profileThemeColorSelectionSource = null;
    this.resetProfileCustomColorPickerState(null);
  }

  private resetProfileCustomColorPickerState(preserveColor?: string | null): void {
    this.profileCustomColorPickerReady = false;
    this.profileCustomColorPickerUserInteracted = false;
    this.profileCustomThemeColorControl.setValue(preserveColor ?? null, { emitEvent: false });
    setTimeout(() => {
      this.profileCustomColorPickerReady = true;
    });
  }

  private detectProfilePaletteFromPreview(): void {
    const landingProfileId = this.editingLandingProfileId;
    if (!this.profileLogoUrl || !landingProfileId) {
      return;
    }

    this.isDetectingProfilePalette = true;
    this.profilePaletteDetectionCompleted = false;
    firstValueFrom(this.companyService.getLandingProfileLogoBlob(landingProfileId))
      .then((blob) => blob ? this.extractPaletteFromLogoBlob(blob) : [])
      .then((palette) => {
        this.profileDetectedPalette = palette;
      })
      .catch(() => undefined)
      .finally(() => {
        this.isDetectingProfilePalette = false;
        this.profilePaletteDetectionCompleted = true;
      });
  }

  getServiceTranslationKey(serviceType: string): string {
    if (!serviceType) {
      return 'TECHNICAL_SERVICES.HEATING';
    }
    const normalized = serviceType.trim();
    const mapped = serviceTypeTranslationKeys[normalized];
    if (mapped) {
      return `TECHNICAL_SERVICES.${mapped}`;
    }
    return `TECHNICAL_SERVICES.${normalized.replace(/[\s\/\-]+/g, '_').toUpperCase()}`;
  }

  private updateTechnicalServicePreview(): void {
    const flattened = flattenTechnicalServiceOrder(this.orderedTechnicalServiceTypes);
    if (!flattened.length) {
      this.technicalServicePreviewGroups = [];
      return;
    }

    const phonePlaceholder = this.translate.instant('COMPANY_PAGE.TECHNICAL_SERVICE_PREVIEW_PHONE');

    this.technicalServicePreviewGroups = this.serviceCategories
      .map((category) => {
        const services = (this.orderedTechnicalServiceTypes[category.key] || []).map((type) => {
          const serviceLabel = this.translate.instant(this.getServiceTranslationKey(type));
          const name = this.translate.instant('COMPANY_PAGE.TECHNICAL_SERVICE_PREVIEW_PLACEHOLDER', { service: serviceLabel });
          return {
            type,
            name,
            phone: phonePlaceholder
          };
        });
        return { key: category.key, services };
      })
      .filter(group => group.services.length > 0);
  }

  getCategoryDropListId(categoryKey: TechnicalServiceCategoryKey): string {
    return `technical-service-category-${categoryKey.toLowerCase()}`;
  }

  formatAvailability(availability: any): any[] {
    if (!availability) return [];

    type TimeRange = { startTime: string; endTime: string };

    interface DayPattern {
      abbr: string;
      times: TimeRange[];
      key: string;
    }

    const dayPatterns: DayPattern[] = [];

    // 1) Build the "pattern" (full-day time list) for each day
    Object.keys(availability).forEach((day) => {
      const abbr = dayAbbreviations[day] || day;
      const ranges = Array.isArray(availability[day]) ? availability[day] : [];

      const times: TimeRange[] = ranges
        .filter((r: any) => r && r.start && r.end)
        .map((r: any) => ({
          startTime: this.formatTimeGerman(r.start),
          endTime: this.formatTimeGerman(r.end),
        }))
        .filter(t => t.startTime && t.endTime)
        .sort((a, b) => a.startTime.localeCompare(b.startTime));

      if (!times.length) return;

      // key that represents the whole-day pattern of time ranges
      const key = times.map(t => `${t.startTime}-${t.endTime}`).join('|');

      dayPatterns.push({ abbr, times, key });
    });

    // 2) Group days that share the same full-day pattern
    const grouped: Record<string, { days: string[]; times: TimeRange[] }> = {};

    dayPatterns.forEach((p) => {
      if (!grouped[p.key]) {
        grouped[p.key] = { days: [], times: p.times };
      }
      grouped[p.key].days.push(p.abbr);
    });

    // 3) Build final array, sort days within each group and groups by first day
    const result = Object.values(grouped).map(g => {
      const days = [...g.days].sort(
        (a, b) => (dayOrderIndex[a] ?? 99) - (dayOrderIndex[b] ?? 99)
      );
      const times = [...g.times]; // already sorted above
      return { days, times };
    });

    result.sort(
      (a, b) => (dayOrderIndex[a.days[0]] ?? 99) - (dayOrderIndex[b.days[0]] ?? 99)
    );

    return result;
  }

  formatTimeGerman(dateString: string): string {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return ''; // Return empty string for invalid date
    }
    return formatDate(date, 'HH:mm', 'de-DE');
  }

  getCompanyDetails(preserveActiveTabIndex?: number): void {
    this.companyService.getCompanyDetails().subscribe(
      (company: Company) => {
        // Temporarily unsubscribe to avoid triggering `UNSAVED_CHANGES_ALERT`
        this.unsubscribeFromFormChanges();
        if (company) {
          this.company = company; // Store the company object
          if (company.appMode) {
            this.appMode = company.appMode;
          }
          this.configureModeSpecificControls(this.appMode);
          const sanitizedServiceOrder = normalizeTechnicalServiceOrder(company.technicalServiceTypeOrder);
          this.orderedTechnicalServiceTypes = sanitizedServiceOrder;
          this.updateTechnicalServicePreview();

          // Patch the form with company details, including availability times
          this.companyForm.patchValue({
            companyName: company.name,
            companyDisplayEmail: (company as any).displayEmail || '',
            companyPhone: company.phone,
            whatsappNumber: company.whatsappNumber || '',
            address: company.address,
            website: company.website,
            caretakerText: company.caretakerText || '',
            publicLandingPageMessage: typeof company.publicLandingPageMessage === 'string'
              ? company.publicLandingPageMessage.trim()
              : '',
            technicalServiceText: company.technicalServiceText || '',
            multiLandingProfilesEnabled: company.multiLandingProfilesEnabled ?? false,
            showManagerInHowToReachUs: company.showManagerInHowToReachUs ?? false,
            howToReachUsManagerOrder: company.howToReachUsManagerOrder || [],
            showEmergencyAvailability: company.showEmergencyAvailability,
            showAvailability: company.showAvailability,
            showEmergencyNumber112: company.showEmergencyNumber112,
            showEmergencyNumber110: company.showEmergencyNumber110,
            showPoisonInfoCenterHotline: company.showPoisonInfoCenterHotline,
            poisonInfoCenterHotlineNumber: company.poisonInfoCenterHotlineNumber || '',
            showVeterinaryEmergencyHotline: company.showVeterinaryEmergencyHotline,
            veterinaryEmergencyHotlineNumber: company.veterinaryEmergencyHotlineNumber || '',
            showTelephoneCounselingHotline: company.showTelephoneCounselingHotline,
            showChildrenYouthHotline: company.showChildrenYouthHotline,
            showWomenGirlsEmergencyHotline: company.showWomenGirlsEmergencyHotline,
            landingPageEmergencyServicePlacementEnabled: company.landingPageEmergencyServicePlacementEnabled === true,
            showEmergencyNumbers: !!(company.showEmergencyNumber112 || company.showEmergencyNumber110),
            emergencyNumber: company.emergencyNumber,
            themeColor: company.themeColor || '',
            technicalServiceTypeOrder: cloneTechnicalServiceOrder(sanitizedServiceOrder),
          }, { emitEvent: false });
          const backendThemeColor = company.themeColor || null;

          this.themeColorSelectionSource = backendThemeColor ? 'backend' : null;
          if (backendThemeColor) {
            this.showThemeColorRequiredError = false;
            this.selectedThemeColor = backendThemeColor;
            this.setThemeColor(backendThemeColor);
          }

          this.resetCustomColorPickerState(backendThemeColor);

          this.companyForm.get('companyName')?.disable({ emitEvent: false });

          // ⬇️ fill normal availability slots
          this.weekDays.forEach(day => {
            const arr = this.getDaySlots(day, false);
            while (arr.length) arr.removeAt(0);
            const ranges = ((this.company?.availability as any)?.[day] || []).slice(0, 2); // <---
            (Array.isArray(ranges) ? ranges : []).forEach((r: any) => {
              arr.push(this.createSlot(r.start ? new Date(r.start) : null, r.end ? new Date(r.end) : null));
            });
          });

          // ⬇️ fill emergency availability slots
          this.emergencyWeekDays.forEach(day => {
            const arr = this.getDaySlots(day, true);
            while (arr.length) arr.removeAt(0);
            const ranges = ((this.company?.emergencyAvailability as any)?.[day] || []).slice(0, 2); // <---
            (Array.isArray(ranges) ? ranges : []).forEach((r: any) => {
              arr.push(this.createSlot(r.start ? new Date(r.start) : null, r.end ? new Date(r.end) : null));
            });
          });

          // ⬇️ seed one empty row when there are no slots (nicer UX)
          this.weekDays.forEach(day => {
            const arr = this.getDaySlots(day, false);
            if (arr.length === 0) arr.push(this.createSlot());
          });
          this.emergencyWeekDays.forEach(day => {
            const arr = this.getDaySlots(day, true);
            if (arr.length === 0) arr.push(this.createSlot());
          });

          // Re-apply validators after loading data from the server
          this.applyEmergencyNumberValidators(!!company.showEmergencyAvailability);
          this.applyPoisonHotlineValidators(!!company.showPoisonInfoCenterHotline);
          this.applyVeterinaryEmergencyHotlineValidators(!!company.showVeterinaryEmergencyHotline);

          // Update the selected theme color
          this.selectedThemeColor = company.themeColor || this.selectedThemeColor;
          this.setThemeColor(this.selectedThemeColor);

          // Fetch and convert logo blob to base64 if available
          if (company.logoUrl) {
            // store the S3 key so saveCompanyInfo() can send it back later
            this.uploadedLogoKey = company.logoUrl;
            this.logoUrl = '';
            this.detectedPalette = [];
            this.paletteDetectionCompleted = false;
            this.logoPreviewState = 'loading';

            // fetch the actual image bytes
            this.companyService.getCompanyLogoBlob().subscribe({
              next: blob => {
                if (!blob) {
                  this.logoUrl = '';
                  this.detectedPalette = [];
                  this.paletteDetectionCompleted = true;
                  this.logoPreviewState = 'empty';
                  return;
                }
                // create a URL for <img [src]="logoUrl">
                this.logoUrl = URL.createObjectURL(blob);
                this.logoPreviewState = 'ready';

                // Extract palette for existing logo too
                this.isDetectingPalette = true;
                this.extractPaletteFromLogoBlob(blob)
                  .then((palette) => this.applyDetectedPalette(palette))
                  .catch(() => {
                    // non-fatal
                  })
                  .finally(() => {
                    this.isDetectingPalette = false;
                    this.paletteDetectionCompleted = true;
                  });
              },
              error: () => {
                this.logoUrl = '';
                this.detectedPalette = [];
                this.paletteDetectionCompleted = true;
                this.logoPreviewState = 'error';
              }
            });
          } else {
            this.logoUrl = '';
            this.detectedPalette = [];
            this.paletteDetectionCompleted = false;
            this.logoPreviewState = 'empty';
          }

          this.showAvailability = company.showAvailability;
          this.showEmergencyAvailability = company.showEmergencyAvailability;
          this.showEmergencyNumber112 = !!company.showEmergencyNumber112;
          this.showEmergencyNumber110 = !!company.showEmergencyNumber110;
          this.showPoisonInfoCenterHotline = !!company.showPoisonInfoCenterHotline;
          this.showVeterinaryEmergencyHotline = !!company.showVeterinaryEmergencyHotline;
          this.showTelephoneCounselingHotline = !!company.showTelephoneCounselingHotline;
          this.showChildrenYouthHotline = !!company.showChildrenYouthHotline;
          this.showWomenGirlsEmergencyHotline = !!company.showWomenGirlsEmergencyHotline;

          if (company.multiLandingProfilesEnabled) {
            this.loadLandingProfiles();
          } else {
            this.landingProfiles = [];
            this.cancelLandingProfileEdit();
          }

          this.setQuickLinks(company.quickLinks);

          if (this.companyForm.contains('hospitalityLanding')) {
            this.setHospitalityLanding(company.hospitalityLanding);
          }
          if (this.companyForm.contains('hospitalityEntries')) {
            this.setHospitalityEntries({
              houseRules: company.hospitalityHouseRules,
              checkIns: company.hospitalityCheckIns,
              checkOuts: company.hospitalityCheckOuts,
              applianceGuides: company.hospitalityApplianceGuides,
              additionalInfos: company.hospitalityAdditionalInfos,
            });
          }

          this.updateQuickLinksPreview();

          // Format the availability times for display in the component (using your format function)
          const availability = this.company?.availability;
          if (availability){
            this.formattedAvailability = this.formatAvailability(availability);
          }
          const emergencyAvailability = this.company?.emergencyAvailability;
          if (emergencyAvailability){
            this.formattedEmergencyAvailability = this.formatAvailability(emergencyAvailability);
          }

          // Reset `formModified` and mark form as pristine after initial load
          this.formModified = false;
          this.companyForm.markAsPristine();
          this.isFormInitialized = true;
          this.mondayBulkCopyOffered.clear();

          this.syncManagerConfigWithTemplateHeaders();

          // Resubscribe to form changes
          this.subscribeToFormChanges();

          if (typeof preserveActiveTabIndex === 'number') {
            const isDisplayTabInHospitality = this.appMode === 'hospitality' && preserveActiveTabIndex === this.DISPLAY_TAB_INDEX;
            if (!isDisplayTabInHospitality) {
              this.activeTabIndex = preserveActiveTabIndex;
            }
          }
        }
      },
      (error) => {
        // Handle errors if the request to fetch company details fails
        console.error('Error fetching company details:', error);
      }
    );
  }

  // Check if current form values differ from the original values to set `formModified`
  protected markFormDirty(): void {
    this.companyForm.markAsDirty();
    if (this.isFormInitialized && !this.formModified) {
      this.checkIfFormModified();
    }
  }

  checkIfFormModified(): void {
    if (!this.companyForm.pristine && !this.formModified) {
      this.formModified = true;
      this.message.info(this.translate.instant('COMPANY_PAGE.UNSAVED_CHANGES_ALERT'));
    }
  }

  subscribeToFormChanges(): void {
    this.formChangesSubscription = this.companyForm.valueChanges.subscribe(() => {
      if (this.isFormInitialized && !this.formModified) {
        this.checkIfFormModified();
      }
      this.showEmergencyNumber112 = this.companyForm.get('showEmergencyNumber112')?.value;
      this.showEmergencyNumber110 = this.companyForm.get('showEmergencyNumber110')?.value;
      this.updateQuickLinksPreview();
      this.computeHospitalityPreview();
    });
  }

  unsubscribeFromFormChanges(): void {
    if (this.formChangesSubscription) {
      this.formChangesSubscription.unsubscribe();
    }
  }

  // Update CSS variables for theme color dynamically
  setThemeColor(color: unknown): void {
    this.companyThemeService.applyCompanyThemeColor(color);
  }

  handleLogoChange(info: NzUploadChangeParam): void {
    if (info.file.status === 'done') {
      this.highlightLogoUpload = false;
      const response = info.file.response;
      this.logoUrl = response.logoUrl; // signed URL for preview
      this.uploadedLogoKey = response.s3Key; // save the actual key for BE
      this.logoPreviewState = 'ready';
      this.detectedPalette = [];
      this.paletteDetectionCompleted = false;
      this.companyForm.patchValue({ logoUrl: this.logoUrl });

      // Reset custom color picker state so the new render's initialization is controlled
      this.resetCustomColorPickerState(this.companyForm.get('themeColor')?.value || null);

      // ✅ Extract palette from the local uploaded file (no CORS)
      const file: File | undefined = info.file?.originFileObj as File | undefined;
      if (file) {
        this.isDetectingPalette = true;
        this.extractPaletteFromLogoFile(file)
          .then((palette) => this.applyDetectedPalette(palette))
          .catch(() => {
            // non-fatal: just don't auto-set theme
          })
          .finally(() => {
            this.isDetectingPalette = false;
            this.paletteDetectionCompleted = true;
          });
      }
    } else if (info.file.status === 'error') {
      this.isDetectingPalette = false;
      this.showLogoUploadError(info.file?.error);
    }
  }

  private showLogoUploadError(backendError: unknown): void {
    const errorResponse = this.getLogoUploadErrorResponse(backendError);
    const errorStatus = backendError instanceof HttpErrorResponse ? backendError.status : null;

    if (errorResponse?.code === 'LOGO_INVALID_FILE_TYPE') {
      this.message.error(this.getLogoInvalidFileTypeErrorMessage());
    } else if (errorResponse?.code === 'LOGO_FILE_TOO_LARGE' || errorStatus === 413) {
      this.message.error(
        this.translate.instant('COMPANY_PAGE.LOGO_MAX_SIZE_ERROR', {
          maxSize: errorResponse?.maxSizeMb || LOGO_MAX_SIZE_MB,
        })
      );
    } else if (!shouldSuppressGenericError(backendError)) {
      this.message.error(this.translate.instant('COMPANY_PAGE.LOGO_UPLOAD_ERROR'));
    }
  }

  beforeUpload = (file: NzUploadFile, fileList: NzUploadFile[]): boolean => {
    const logoFileType = this.getLogoFileType(file.name || file.originFileObj?.name || '');
    const mimeType = (file.type || file.originFileObj?.type || '').toLowerCase();
    const hasMatchingMimeType = !mimeType || (
      LOGO_ACCEPTED_MIME_TYPES.includes(mimeType) && logoFileType?.mimeType === mimeType
    );

    if (!logoFileType || !hasMatchingMimeType) {
      this.message.error(this.getLogoInvalidFileTypeErrorMessage());
      return false;
    }

    const isLtMaxSize = (file.size || file.originFileObj?.size || 0) < LOGO_MAX_SIZE_BYTES;
    if (!isLtMaxSize) {
      this.message.error(
        this.translate.instant('COMPANY_PAGE.LOGO_MAX_SIZE_ERROR', { maxSize: LOGO_MAX_SIZE_MB })
      );
    }
    return isLtMaxSize;
  };

  private getLogoFileType(fileName: string) {
    const extensionMatch = /\.[^.]+$/.exec(fileName.trim());
    const extension = extensionMatch?.[0].toLowerCase() || '';
    return LOGO_FILE_TYPES.find((fileType) => fileType.extension === extension);
  }

  private getLogoUploadErrorResponse(error: unknown): LogoUploadErrorResponse | null {
    if (!(error instanceof HttpErrorResponse)) {
      return null;
    }

    const response = error.error;
    if (!response || typeof response !== 'object' || Array.isArray(response)) {
      return null;
    }

    const payload = response as Record<string, unknown>;
    return {
      code: typeof payload['code'] === 'string' ? payload['code'] : undefined,
      maxSizeMb: typeof payload['maxSizeMb'] === 'number' ? payload['maxSizeMb'] : undefined,
    };
  }

  private getLogoInvalidFileTypeErrorMessage(): string {
    const supportedFormats = this.translate.instant('COMPANY_PAGE.LOGO_SUPPORTED_FORMATS');
    return this.translate.instant('COMPANY_PAGE.LOGO_INVALID_FILE_TYPE_ERROR', {
      supportedFormats: supportedFormats || LOGO_SUPPORTED_FORMATS.join(', '),
    });
  }

  override removeHospitalityCatalogLink(
    sectionKey: keyof HospitalityCatalogEntries,
    entryIndex: number,
    linkIndex: number,
  ): void {
    super.removeHospitalityCatalogLink(sectionKey, entryIndex, linkIndex);
    this.clearHospitalityLinkDocumentUploadState(sectionKey, entryIndex, linkIndex);
  }

  hospitalityLinkDocumentUploadHandler(
    sectionKey: keyof HospitalityCatalogEntries,
    entryIndex: number,
    linkIndex: number,
  ): (item: NzUploadXHRArgs) => Subscription {
    return (item: NzUploadXHRArgs): Subscription => {
      const uploadFile = this.normalizeNzUploadFile(item);
      const file = uploadFile.originFileObj as File | undefined;
      if (!file) {
        item.onError?.(new Error('No file selected'), uploadFile);
        return new Subscription();
      }
      if (!this.validateHospitalityDocumentSize(file)) {
        item.onError?.(new Error('File too large'), uploadFile);
        return new Subscription();
      }

      const key = this.getHospitalityLinkDocumentKey(sectionKey, entryIndex, linkIndex);
      this.setHospitalityLinkDocumentUploading(key, true);

      const subscription = this.companyService
        .uploadHospitalityLinkDocument(file)
        .pipe(finalize(() => this.setHospitalityLinkDocumentUploading(key, false)))
        .subscribe({
          next: (document) => {
            this.patchHospitalityLinkDocument(sectionKey, entryIndex, linkIndex, document);
            item.onSuccess?.(document, uploadFile, {} as XMLHttpRequest);
          },
          error: (error) => {
            if (!shouldSuppressGenericError(error)) {
              this.message.error(
                this.translate.instant('COMPANY_PAGE.HOSPITALITY_TAB.ERRORS.DOCUMENT_UPLOAD')
              );
            }
            item.onError?.(error, uploadFile);
          },
        });
      return subscription;
    };
  }

  isHospitalityLinkDocumentUploading(
    sectionKey: keyof HospitalityCatalogEntries,
    entryIndex: number,
    linkIndex: number,
  ): boolean {
    const key = this.getHospitalityLinkDocumentKey(sectionKey, entryIndex, linkIndex);
    return this.hospitalityLinkDocumentUploads.has(key);
  }

  removeHospitalityLinkDocument(
    sectionKey: keyof HospitalityCatalogEntries,
    entryIndex: number,
    linkIndex: number,
  ): void {
    this.patchHospitalityLinkDocument(sectionKey, entryIndex, linkIndex, null);
    this.clearHospitalityLinkDocumentUploadState(sectionKey, entryIndex, linkIndex);
  }

  private patchHospitalityLinkDocument(
    sectionKey: keyof HospitalityCatalogEntries,
    entryIndex: number,
    linkIndex: number,
    document: HospitalityLinkDocument | null,
  ): void {
    const linkGroup = this.getHospitalityLinkFormGroup(sectionKey, entryIndex, linkIndex);
    if (!linkGroup) {
      return;
    }
    const control = linkGroup.get('document') as FormControl | null;
    if (!control) {
      return;
    }
    control.setValue(document);
    control.markAsDirty();
    control.markAsTouched();
    control.updateValueAndValidity({ emitEvent: false });
    linkGroup.markAsDirty();
    this.markFormDirty();
  }

  private validateHospitalityDocumentSize(file: File): boolean {
    if (!file?.size) {
      return true;
    }
    const maxBytes = this.HOSPITALITY_DOCUMENT_MAX_SIZE_MB * 1024 * 1024;
    if (file.size <= maxBytes) {
      return true;
    }
    this.message.error(
      this.translate.instant('COMPANY_PAGE.HOSPITALITY_TAB.ERRORS.DOCUMENT_TOO_LARGE', {
        maxSize: this.HOSPITALITY_DOCUMENT_MAX_SIZE_MB,
      })
    );
    return false;
  }

  private getHospitalityLinkFormGroup(
    sectionKey: keyof HospitalityCatalogEntries,
    entryIndex: number,
    linkIndex: number,
  ): FormGroup | null {
    const links = this.hospitalityCatalogLinks(sectionKey, entryIndex);
    const control = links?.at(linkIndex) || null;
    return control instanceof FormGroup ? control : null;
  }

  private getHospitalityLinkDocumentKey(
    sectionKey: keyof HospitalityCatalogEntries,
    entryIndex: number,
    linkIndex: number,
  ): string {
    return `${sectionKey}:${entryIndex}:${linkIndex}`;
  }

  private setHospitalityLinkDocumentUploading(key: string, uploading: boolean): void {
    if (uploading) {
      this.hospitalityLinkDocumentUploads.add(key);
    } else {
      this.hospitalityLinkDocumentUploads.delete(key);
    }
  }

  private clearHospitalityLinkDocumentUploadState(
    sectionKey: keyof HospitalityCatalogEntries,
    entryIndex: number,
    linkIndex?: number,
  ): void {
    const prefix = `${sectionKey}:${entryIndex}:`;
    const keys = Array.from(this.hospitalityLinkDocumentUploads);
    keys.forEach((key) => {
      if (key.startsWith(prefix)) {
        if (linkIndex === undefined || key === this.getHospitalityLinkDocumentKey(sectionKey, entryIndex, linkIndex)) {
          this.hospitalityLinkDocumentUploads.delete(key);
        }
      }
    });
  }

  private normalizeNzUploadFile(item: NzUploadXHRArgs): NzUploadFile {
    const uploadFile = item.file as NzUploadFile | undefined;
    if (uploadFile?.uid) {
      if (!uploadFile.originFileObj && item.postFile instanceof File) {
        uploadFile.originFileObj = item.postFile;
      }
      return uploadFile;
    }

    const rawFile =
      (uploadFile?.originFileObj as File | undefined) ||
      (item.postFile instanceof File ? item.postFile : undefined) ||
      undefined;

    const normalized: NzUploadFile = {
      uid: uploadFile?.uid || `${Date.now()}`,
      name: uploadFile?.name || rawFile?.name || 'file',
      status: uploadFile?.status,
      type: uploadFile?.type || rawFile?.type,
      size: uploadFile?.size || rawFile?.size,
      originFileObj: rawFile,
    };

    item.file = normalized;
    return normalized;
  }

  // Save company information
  saveCompanyInfo(): void {
    if (this.isSaving) {
      return;
    }

    const websiteControl = this.companyForm.get('website');
    if (typeof websiteControl?.value === 'string' && !websiteControl.value.trim()) {
      websiteControl.setValue('', { emitEvent: false });
    }

    const formValues = this.companyForm.getRawValue();

    const missingLogo = !this.uploadedLogoKey && !this.company?.logoUrl;
    const missingThemeColor = !this.themeColorSelectionSource || !this.companyForm.get('themeColor')?.value;

    if (missingLogo || missingThemeColor) {
      this.markFormGroupForValidation(this.companyForm);
      this.activateFirstInvalidTab();

      if (missingLogo && missingThemeColor) {
        this.highlightLogoUpload = true;
        this.showThemeColorRequiredError = true;

        const themeColorControl = this.companyForm.get('themeColor');
        const errors = { ...(themeColorControl?.errors || {}) };
        errors['required'] = true;
        themeColorControl?.setErrors(errors);
        themeColorControl?.markAsTouched();
        themeColorControl?.markAsDirty();

        this.message.error(this.translate.instant('COMPANY_PAGE.LOGO_AND_THEME_REQUIRED'));
      } else if (missingLogo) {
        this.highlightLogoUpload = true;
        this.message.error(this.translate.instant('COMPANY_PAGE.LOGO_REQUIRED_ERROR'));
      } else {
        this.showThemeColorRequiredError = true;

        const themeColorControl = this.companyForm.get('themeColor');
        const errors = { ...(themeColorControl?.errors || {}) };
        errors['required'] = true;
        themeColorControl?.setErrors(errors);
        themeColorControl?.markAsTouched();
        themeColorControl?.markAsDirty();

        this.message.error(this.translate.instant('COMPANY_PAGE.THEME_COLOR_REQUIRED_ERROR'));
      }

      return;
    }

    if (!this.companyForm.valid) {
      this.markFormGroupForValidation(this.companyForm);
      this.activateFirstInvalidTab();

      // Check and show specific validation error messages
      if (this.companyForm.get('companyName')?.hasError('required')) {
        this.message.error(this.translate.instant('COMPANY_PAGE.NAME_REQUIRED_ERROR'));
      }

      if (this.companyForm.get('companyDisplayEmail')?.hasError('email')) {
        this.message.error(this.translate.instant('COMPANY_PAGE.EMAIL_ERROR'));
      }

      if (this.companyForm.get('companyPhone')?.hasError('invalidPhone')) {
        this.message.error(this.translate.instant('COMPANY_PAGE.PHONE_ERROR'));
      } else if (this.companyForm.get('companyPhone')?.hasError('required')) {
        this.message.error(this.translate.instant('COMPANY_PAGE.PHONE_REQUIRED_ERROR'));
      }

      if (this.companyForm.get('whatsappNumber')?.hasError('invalidWhatsAppNumber')) {
        this.message.error(this.translate.instant('COMPANY_PAGE.WHATSAPP_NUMBER_ERROR'));
      }

      if (this.appMode === 'hospitality') {
        const hostPhoneControl = this.companyForm.get('hospitalityLanding.hostContact.phone');
        const feedbackPhoneControl = this.companyForm.get('hospitalityLanding.feedback.phone');
        if (hostPhoneControl?.hasError('invalidPhone') || feedbackPhoneControl?.hasError('invalidPhone')) {
          this.message.error(this.translate.instant('COMPANY_PAGE.HOSPITALITY_TAB.ERRORS.PHONE_INVALID'));
        }

        const hospitalityEntriesGroup = this.companyForm.get('hospitalityEntries');
        const checkInEntries = hospitalityEntriesGroup?.get('checkIns') as FormArray | null;
        const checkOutEntries = hospitalityEntriesGroup?.get('checkOuts') as FormArray | null;
        const hasInvalidHospitalityAddress =
          !!checkInEntries?.controls.some((ctrl) => ctrl.get('location')?.hasError('mapAddress')) ||
          !!checkOutEntries?.controls.some((ctrl) => ctrl.get('dropOffLocation')?.hasError('mapAddress'));

        if (hasInvalidHospitalityAddress) {
          this.message.error(this.translate.instant('COMPANY_PAGE.HOSPITALITY_TAB.ERRORS.LOCATION_INVALID'));
        }
      }

      if (this.companyForm.get('showEmergencyAvailability')?.value) {
        if (this.companyForm.get('emergencyNumber')?.hasError('invalidPhone')) {
          this.message.error(this.translate.instant('COMPANY_PAGE.EMERGENCY_PHONE_ERROR'));
        } else if (this.companyForm.get('emergencyNumber')?.hasError('required')) {
          this.message.error(this.translate.instant('COMPANY_PAGE.EMERGENCY_PHONE_REQUIRED_ERROR'));
        }
      }

      if (this.companyForm.get('showPoisonInfoCenterHotline')?.value) {
        if (this.companyForm.get('poisonInfoCenterHotlineNumber')?.hasError('invalidPhone')) {
          this.message.error(this.translate.instant('COMPANY_PAGE.POISON_INFO_CENTER_HOTLINE_NUMBER_ERROR'));
        } else if (this.companyForm.get('poisonInfoCenterHotlineNumber')?.hasError('required')) {
          this.message.error(this.translate.instant('COMPANY_PAGE.POISON_INFO_CENTER_HOTLINE_NUMBER_REQUIRED_ERROR'));
        }
      }

      if (this.companyForm.get('showVeterinaryEmergencyHotline')?.value) {
        if (this.companyForm.get('veterinaryEmergencyHotlineNumber')?.hasError('invalidPhone')) {
          this.message.error(this.translate.instant('COMPANY_PAGE.VETERINARY_EMERGENCY_HOTLINE_NUMBER_ERROR'));
        } else if (this.companyForm.get('veterinaryEmergencyHotlineNumber')?.hasError('required')) {
          this.message.error(this.translate.instant('COMPANY_PAGE.VETERINARY_EMERGENCY_HOTLINE_NUMBER_REQUIRED_ERROR'));
        }
      }

      if (this.companyForm.get('website')?.hasError('invalidUrl')) {
        this.message.error(this.translate.instant('COMPANY_PAGE.WEBSITE_ERROR'));
      }

      if (this.companyForm.get('caretakerText')?.hasError('required')) {
        this.message.error(this.translate.instant('COMPANY_PAGE.CARETAKER_TEXT_REQUIRED_ERROR'));
      }

      if (this.companyForm.get('technicalServiceText')?.hasError('required')) {
        this.message.error(this.translate.instant('COMPANY_PAGE.TECHNICAL_SERVICE_TEXT_REQUIRED_ERROR'));
      }

      if (this.companyForm.get('address')?.hasError('required')) {
        this.message.error(this.translate.instant('COMPANY_PAGE.ADDRESS_REQUIRED_ERROR'));
      } else if (this.companyForm.get('address')?.hasError('invalidAddress')) {
        this.message.error(this.translate.instant('COMPANY_PAGE.ADDRESS_ERROR'));
      }

      if (this.companyForm.get('showAvailability')?.value) {
        const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
        weekDays.forEach(day => {
          if (this.companyForm.get(`${day}StartTime`)?.hasError('required')) {
            this.message.error(this.translate.instant(`COMPANY_PAGE.${day.toUpperCase()}_START_REQUIRED_ERROR`));
          }
          if (this.companyForm.get(`${day}EndTime`)?.hasError('required')) {
            this.message.error(this.translate.instant(`COMPANY_PAGE.${day.toUpperCase()}_END_REQUIRED_ERROR`));
          }
        });
      }

      if (this.companyForm.get('showEmergencyAvailability')?.value) {
        const emergencyWeekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        emergencyWeekDays.forEach(day => {
          if (this.companyForm.get(`Emergency${day}StartTime`)?.hasError('required')) {
            this.message.error(this.translate.instant(`COMPANY_PAGE.${day.toUpperCase()}_START_REQUIRED_ERROR`));
          }
          if (this.companyForm.get(`Emergency${day}EndTime`)?.hasError('required')) {
            this.message.error(this.translate.instant(`COMPANY_PAGE.${day.toUpperCase()}_END_REQUIRED_ERROR`));
          }
        });
      }

      if (this.companyForm.hasError('availabilityRequired')) {
        this.message.error(this.translate.instant('COMPANY_PAGE.ONE_DAY_AVAILABILITY_REQUIRED'));
      }
      if (this.companyForm.hasError('emergencyAvailabilityRequired')) {
        this.message.error(this.translate.instant('COMPANY_PAGE.ONE_DAY_EMERGENCY_AVAILABILITY_REQUIRED'));
      }
      if (this.companyForm.hasError('partialAvailability')) {
        this.message.error(this.translate.instant('COMPANY_PAGE.PARTIAL_AVAILABILITY_ERROR'));
      }
      if (this.companyForm.hasError('partialEmergencyAvailability')) {
        this.message.error(this.translate.instant('COMPANY_PAGE.PARTIAL_EMERGENCY_AVAILABILITY_ERROR'));
      }

      // Quick links validation feedback
      const invalidQuickLinkIndex = this.quickLinks.controls.findIndex(
        (ctrl) => (ctrl as FormGroup).invalid
      );
      if (invalidQuickLinkIndex !== -1) {
        this.message.error(
          this.translate.instant('COMPANY_PAGE.QUICK_LINKS_INCOMPLETE_ERROR', { index: invalidQuickLinkIndex + 1 })
        );
      }


      return;
    }

    if (formValues.showManagerInHowToReachUs && (!Array.isArray(formValues.howToReachUsManagerOrder) || formValues.howToReachUsManagerOrder.length === 0)) {
      this.message.error(this.translate.instant('COMPANY_PAGE.HOW_TO_REACH_US_MANAGER_ORDER_REQUIRED'));
      return;
    }
      
      // helpers to build payload arrays
      const slotsToPayload = (day: string, emergency = false) => {
        const arr = this.getDaySlots(day, emergency);
        return arr.controls
          .map(c => {
            const s: Date | null = c.get('start')?.value;
            const e: Date | null = c.get('end')?.value;
            return (s && e) ? { start: s.toISOString(), end: e.toISOString() } : null;
          })
          .filter(Boolean) as { start: string; end: string }[];
      };

      const availabilityPayload = formValues.showAvailability ? {
        Monday:    slotsToPayload('Monday'),
        Tuesday:   slotsToPayload('Tuesday'),
        Wednesday: slotsToPayload('Wednesday'),
        Thursday:  slotsToPayload('Thursday'),
        Friday:    slotsToPayload('Friday'),
      } : null;

      const emergencyAvailabilityPayload = formValues.showEmergencyAvailability ? {
        Monday:    slotsToPayload('Monday', true),
        Tuesday:   slotsToPayload('Tuesday', true),
        Wednesday: slotsToPayload('Wednesday', true),
        Thursday:  slotsToPayload('Thursday', true),
        Friday:    slotsToPayload('Friday', true),
        Saturday:  slotsToPayload('Saturday', true),
        Sunday:    slotsToPayload('Sunday', true),
      } : null;

      const hospitalityLanding = this.appMode === 'hospitality'
        ? this.extractHospitalityLanding()
        : null;
      const hospitalityEntries = this.appMode === 'hospitality'
        ? this.extractHospitalityEntries()
        : null;

      const formattedData: any = {
        companyName: formValues.companyName,
        companyDisplayEmail:
          (formValues.companyDisplayEmail && String(formValues.companyDisplayEmail).trim()) || null,
        companyPhone: formValues.companyPhone,
        whatsappNumber: normalizeWhatsAppNumber(formValues.whatsappNumber),
        address: formValues.address,
        website: formValues.website,
        caretakerText: formValues.caretakerText,
        publicLandingPageMessage:
          (formValues.publicLandingPageMessage && String(formValues.publicLandingPageMessage).trim()) || null,
        technicalServiceText: formValues.technicalServiceText,
        showPropertyManager: formValues.showPropertyManager,
        multiLandingProfilesEnabled: !!formValues.multiLandingProfilesEnabled,
        showSevManager: formValues.showSevManager,
        showAccountant: formValues.showAccountant,
        showManagerInHowToReachUs: !!formValues.showManagerInHowToReachUs,
        howToReachUsManagerOrder: Array.isArray(formValues.howToReachUsManagerOrder)
          ? formValues.howToReachUsManagerOrder
          : [],
        showEmergencyAvailability: formValues.showEmergencyAvailability,
        showAvailability: formValues.showAvailability,
        showTechnicalAssetsManager: formValues.showTechnicalAssetsManager,
        showEmergencyNumber112: !!formValues.showEmergencyNumber112,
        showEmergencyNumber110: !!formValues.showEmergencyNumber110,
        showPoisonInfoCenterHotline: !!formValues.showPoisonInfoCenterHotline,
        poisonInfoCenterHotlineNumber:
          (formValues.poisonInfoCenterHotlineNumber && String(formValues.poisonInfoCenterHotlineNumber).trim()) || null,
        showVeterinaryEmergencyHotline: !!formValues.showVeterinaryEmergencyHotline,
        veterinaryEmergencyHotlineNumber:
          formValues.showVeterinaryEmergencyHotline
            ? (formValues.veterinaryEmergencyHotlineNumber && String(formValues.veterinaryEmergencyHotlineNumber).trim()) || null
            : null,
        showTelephoneCounselingHotline: !!formValues.showTelephoneCounselingHotline,
        showChildrenYouthHotline: !!formValues.showChildrenYouthHotline,
        showWomenGirlsEmergencyHotline: !!formValues.showWomenGirlsEmergencyHotline,
        landingPageEmergencyServicePlacementEnabled: !!formValues.landingPageEmergencyServicePlacementEnabled,
        emergencyNumber: (formValues.emergencyNumber && String(formValues.emergencyNumber).trim()) || null,
        themeColor: this.extractColorPickerValue(formValues.themeColor) || null,
        logoUrl: this.uploadedLogoKey || this.company?.logoUrl,
        quickLinks: this.quickLinks.controls
          .map(control => control.value as CompanyQuickLink)
          .filter(link => link.label?.trim() && link.url?.trim()),

        // Only include availability data if showAvailability is true
        availability: availabilityPayload,
        emergencyAvailability: emergencyAvailabilityPayload,
      };

      if (hospitalityLanding) {
        formattedData.hospitalityLanding = hospitalityLanding;
      }
      if (hospitalityEntries) {
        formattedData.hospitalityEntries = hospitalityEntries;
      }
      this.isSaving = true;

      const logoKeyBeforeSave = this.company?.logoUrl || null;
      const logoKeyBeingSaved = this.uploadedLogoKey || this.company?.logoUrl || null;
      const logoKeyChanged = logoKeyBeforeSave !== logoKeyBeingSaved;

      // Call the backend service to save company info
      this.companyService
        .updateCompany(formattedData)
        .pipe(finalize(() => (this.isSaving = false)))
        .subscribe(
          (response) => {
            const previousTabIndex = this.activeTabIndex;
            this.showAvailability = formattedData.showAvailability;
            this.showEmergencyAvailability = formattedData.showEmergencyAvailability;

            if (logoKeyChanged) {
              this.getCompanyDetails(previousTabIndex);
            } else {
              this.applyCompanySaveSuccessLocally(response, formattedData);
            }

            this.formModified = false;
            this.companyForm.markAsPristine();
            this.message.success(this.translate.instant('COMPANY_PAGE.UPDATE_SUCCESS'));
            this.progressService.notifyProgressUpdated();
          },
          (error) => {
            console.error('Error updating company info', error);
            if (!shouldSuppressGenericError(error)) {
              this.message.error(this.translate.instant('COMPANY_PAGE.UPDATE_ERROR'));
            }
          }
      );
  }

  private applyCompanySaveSuccessLocally(response: any, formattedData: any): void {
    const updatedFields = this.extractUpdatedCompanyFromSaveResponse(response) || {};

    this.company = {
      ...this.company,
      ...(updatedFields as Company),
      themeColor: this.extractColorPickerValue(formattedData.themeColor) || undefined,
      logoUrl: this.uploadedLogoKey || this.company?.logoUrl || updatedFields.logoUrl,
    };

    const savedThemeColor = this.company?.themeColor;
    if (savedThemeColor) {
      this.selectedThemeColor = savedThemeColor;
      this.setThemeColor(savedThemeColor);
    }
  }

  private extractUpdatedCompanyFromSaveResponse(response: any): Partial<Company> | null {
    if (!response) return null;

    if (this.looksLikeCompanyResponse(response.company)) return response.company;
    if (this.looksLikeCompanyResponse(response.data?.company)) return response.data.company;
    if (this.looksLikeCompanyResponse(response.data)) return response.data;
    if (this.looksLikeCompanyResponse(response)) return response;

    return null;
  }

  private looksLikeCompanyResponse(value: any): value is Partial<Company> {
    return !!value
      && typeof value === 'object'
      && !Array.isArray(value)
      && (
        typeof value._id === 'string'
        || typeof value.id === 'string'
        || typeof value.companyName === 'string'
        || typeof value.name === 'string'
        || typeof value.logoUrl === 'string'
        || typeof value.themeColor === 'string'
        || typeof value.email === 'string'
      );
  }

  override addHospitalityCatalogEntry(sectionKey: keyof HospitalityCatalogEntries): void {
    const array = this.getHospitalityCatalogArray(sectionKey);
    const newEntryIndex = array ? array.length : 0;

    super.addHospitalityCatalogEntry(sectionKey);

    this.scrollToHospitalityEntryEditor(sectionKey, newEntryIndex);
  }

  override onHospitalitySectionActiveChange(
    sectionKey: keyof HospitalityCatalogEntries,
    expanded: boolean
  ): void {
    super.onHospitalitySectionActiveChange(sectionKey, expanded);
    if (expanded) {
      this.scrollHospitalitySectionIntoView(sectionKey);
    }
  }

  private scrollHospitalitySectionIntoView(sectionKey: keyof HospitalityCatalogEntries): void {
    if (sectionKey !== 'additionalInfos') {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const target = this.additionalInfosSection?.nativeElement;
    if (!target) {
      return;
    }

    const scrollToSection = () => target.scrollIntoView({ behavior: 'smooth', block: 'start' });

    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(scrollToSection);
    } else {
      setTimeout(scrollToSection, 0);
    }
  }

  private scrollToHospitalityEntryEditor(sectionKey: keyof HospitalityCatalogEntries, index: number): void {
    if (typeof window === 'undefined') {
      return;
    }

    const attemptScroll = (remainingAttempts: number) => {
      const doc = window.document;
      const formId = `${sectionKey}-form-${index}`;
      const previewId = `${sectionKey}-preview-${index}`;
      const target = doc.getElementById(formId) || doc.getElementById(previewId);

      if (!target) {
        if (remainingAttempts > 0) {
          setTimeout(() => attemptScroll(remainingAttempts - 1), 50);
        }
        return;
      }

      target.scrollIntoView({ behavior: 'smooth', block: 'start' });

      const focusTarget = target.querySelector<HTMLElement>('textarea, input, select, button, [tabindex]');
      if (focusTarget && typeof focusTarget.focus === 'function') {
        focusTarget.focus({ preventScroll: true });
      }
    };

    const schedule = () => attemptScroll(5);

    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(schedule);
    } else {
      schedule();
    }
  }

  private activateFirstInvalidTab(): void {
    const tabIndex = this.getFirstInvalidTabIndex();
    if (tabIndex !== null && tabIndex !== undefined) {
      this.activeTabIndex = tabIndex;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const revealInvalidControl = () => {
      const activePane = window.document.querySelector('.company-tabs .ant-tabs-tabpane-active');
      if (!activePane) {
        return;
      }

      const target = activePane.querySelector<HTMLElement>(
        'input.ng-invalid, textarea.ng-invalid, .ng-invalid .ant-select-selector, .field-error, .ant-form-item-has-error'
      );

      if (!target) {
        return;
      }

      target.scrollIntoView({ behavior: 'smooth', block: 'center' });

      const focusTarget =
        target.matches('input, textarea, select, button, [tabindex]')
          ? target
          : target.querySelector<HTMLElement>('input, textarea, select, button, [tabindex], .ant-select-selector');

      if (focusTarget && typeof focusTarget.focus === 'function') {
        focusTarget.focus({ preventScroll: true });
      }
    };

    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => window.requestAnimationFrame(revealInvalidControl));
    } else {
      setTimeout(revealInvalidControl, 0);
    }
  }

  private getFirstInvalidTabIndex(): number | null {
    return this.isHospitality
      ? this.getFirstInvalidHospitalityTabIndex()
      : this.getFirstInvalidEmergencyTabIndex();
  }

  private getFirstInvalidEmergencyTabIndex(): number | null {
    const generalInvalid =
      this.isControlInvalid('companyName')
      || this.isControlInvalid('companyDisplayEmail')
      || this.isControlInvalid('companyPhone')
      || this.isControlInvalid('whatsappNumber')
      || this.isControlInvalid('address')
      || this.isControlInvalid('website')
      || this.isControlInvalid('caretakerText')
      || this.isControlInvalid('publicLandingPageMessage')
      || this.isControlInvalid('technicalServiceText')
      || this.isControlInvalid('themeColor');
    if (generalInvalid) {
      return 0;
    }

    const availabilityInvalid =
      this.companyForm.hasError('availabilityRequired')
      || this.companyForm.hasError('partialAvailability')
      || this.weekDays.some((day) => this.isArrayInvalid(`${day}Slots`));
    if (availabilityInvalid) {
      return 1;
    }

    const emergencyInvalid =
      this.isControlInvalid('emergencyNumber')
      || this.companyForm.hasError('emergencyAvailabilityRequired')
      || this.companyForm.hasError('partialEmergencyAvailability')
      || this.emergencyWeekDays.some((day) => this.isArrayInvalid(`Emergency${day}Slots`));
    if (emergencyInvalid) {
      return 2;
    }

    const displayInvalid =
      this.isControlInvalid('poisonInfoCenterHotlineNumber')
      || this.isControlInvalid('veterinaryEmergencyHotlineNumber');
    if (displayInvalid) {
      return this.DISPLAY_TAB_INDEX;
    }

    const linksInvalid = this.quickLinks.controls.some((ctrl) => ctrl.invalid);
    if (linksInvalid) {
      return this.DISPLAY_TAB_INDEX + 2;
    }

    return null;
  }

  private getFirstInvalidHospitalityTabIndex(): number | null {
    const generalInvalid =
      this.isControlInvalid('companyName')
      || this.isControlInvalid('companyDisplayEmail')
      || this.isControlInvalid('companyPhone')
      || this.isControlInvalid('whatsappNumber')
      || this.isControlInvalid('address')
      || this.isControlInvalid('website')
      || this.isControlInvalid('themeColor');
    if (generalInvalid) {
      return 0;
    }

    const landingInvalid = this.isControlInvalid('hospitalityLanding.welcomeMessage')
      || this.isControlInvalid('hospitalityLanding.guestNotes');
    if (landingInvalid) {
      return 1;
    }

    const hostContactInvalid =
      this.isControlInvalid('hospitalityLanding.hostContact.name')
      || this.isControlInvalid('hospitalityLanding.hostContact.phone')
      || this.isControlInvalid('hospitalityLanding.hostContact.email')
      || this.isControlInvalid('hospitalityLanding.hostContact.whatsapp')
      || this.isControlInvalid('hospitalityLanding.hostContact.supportHours')
      || this.isArrayInvalid('hospitalityLanding.hostContact.supportHoursSlots');
    if (hostContactInvalid) {
      return 2;
    }

    const hospitalityEntriesControl = this.companyForm.get('hospitalityEntries');
    if (hospitalityEntriesControl?.invalid) {
      return 3;
    }

    const feedbackInvalid =
      this.isControlInvalid('hospitalityLanding.feedback.instructions')
      || this.isControlInvalid('hospitalityLanding.feedback.email')
      || this.isControlInvalid('hospitalityLanding.feedback.phone')
      || this.isControlInvalid('hospitalityLanding.feedback.formUrl');
    if (feedbackInvalid) {
      return 4;
    }

    return null;
  }

  private isControlInvalid(path: string): boolean {
    const control = this.companyForm.get(path);
    return !!(control && control.enabled && control.invalid);
  }

  private isArrayInvalid(path: string): boolean {
    const control = this.companyForm.get(path);
    if (!(control instanceof FormArray) || !control.enabled) {
      return false;
    }
    return control.invalid || control.controls.some((item) => item.invalid);
  }

  validateAtLeastOneAvailability(control: AbstractControl): { [key: string]: boolean } | null {
    const form = control as FormGroup;
    if (!form?.get('showAvailability')?.value) return null;
    const days = ['Monday','Tuesday','Wednesday','Thursday','Friday'];
    const ok = days.some(day => {
      const arr = form.get(`${day}Slots`) as FormArray;
      return arr?.controls?.some(c => c.get('start')?.value && c.get('end')?.value);
    });
    return ok ? null : { availabilityRequired: true };
  }

  validateAtLeastOneEmergencyAvailability(control: AbstractControl): { [key: string]: boolean } | null {
    const form = control as FormGroup;
    if (!form?.get('showEmergencyAvailability')?.value) return null;
    const days = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
    const ok = days.some(day => {
      const arr = form.get(`Emergency${day}Slots`) as FormArray;
      return arr?.controls?.some(c => c.get('start')?.value && c.get('end')?.value);
    });
    return ok ? null : { emergencyAvailabilityRequired: true };
  }

  validateCompleteAvailability(control: AbstractControl): { [key: string]: boolean } | null {
    const form = control as FormGroup;
    if (!form?.get('showAvailability')?.value) return null;
    const days = ['Monday','Tuesday','Wednesday','Thursday','Friday'];
    for (const day of days) {
      const arr = form.get(`${day}Slots`) as FormArray;
      for (const c of arr.controls) {
        const s = c.get('start')?.value, e = c.get('end')?.value;
        if ((s && !e) || (!s && e)) return { partialAvailability: true };
      }
    }
    return null;
  }

  validateCompleteEmergencyAvailability(control: AbstractControl): { [key: string]: boolean } | null {
    const form = control as FormGroup;
    if (!form?.get('showEmergencyAvailability')?.value) return null;
    const days = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
    for (const day of days) {
      const arr = form.get(`Emergency${day}Slots`) as FormArray;
      for (const c of arr.controls) {
        const s = c.get('start')?.value, e = c.get('end')?.value;
        if ((s && !e) || (!s && e)) return { partialEmergencyAvailability: true };
      }
    }
    return null;
  }

  canAddQuickLink(): boolean {
    if (this.quickLinks.length === 0) return true;
    const last = this.quickLinks.at(this.quickLinks.length - 1) as FormGroup;
    return last.valid;
  }

  onShowEmergencyNumbersChange(checked: boolean): void {
    // keep local booleans (used by preview in this component) in sync
    this.showEmergencyNumber112 = checked;
    this.showEmergencyNumber110 = checked;

    // ensure underlying individual form controls mirror the combined switch
    this.companyForm.patchValue(
      {
        showEmergencyNumber112: checked,
        showEmergencyNumber110: checked
      },
      { emitEvent: false }
    );
  }

  private markFormGroupForValidation(control: AbstractControl): void {
    control.markAsTouched();
    control.markAsDirty();
    control.updateValueAndValidity();

    if (control instanceof FormGroup || control instanceof FormArray) {
      Object.values(control.controls).forEach(child => {
        this.markFormGroupForValidation(child);
      });
    }
  }

  private applyEmergencyNumberValidators(enabled: boolean): void {
    const ctrl = this.companyForm.get('emergencyNumber');
    if (!ctrl) return;

    if (enabled) {
      ctrl.setValidators([Validators.required, (c: AbstractControl) => this.phoneValidator(c)]);
    } else {
      ctrl.clearValidators();
      ctrl.markAsPristine();
      ctrl.markAsUntouched();
    }
    ctrl.updateValueAndValidity();
  }

  private applyPoisonHotlineValidators(enabled: boolean): void {
    const ctrl = this.companyForm.get('poisonInfoCenterHotlineNumber');
    if (!ctrl) return;

    if (enabled) {
      ctrl.setValidators([
        Validators.required,
        Validators.maxLength(this.GENERAL_LIMITS.emergencyNumber),
        (c: AbstractControl) => this.phoneValidator(c)
      ]);
    } else {
      ctrl.setValidators([Validators.maxLength(this.GENERAL_LIMITS.emergencyNumber)]);
      ctrl.markAsPristine();
      ctrl.markAsUntouched();
    }

    ctrl.updateValueAndValidity();
  }

  private applyVeterinaryEmergencyHotlineValidators(enabled: boolean): void {
    const ctrl = this.companyForm.get('veterinaryEmergencyHotlineNumber');
    if (!ctrl) return;

    if (enabled) {
      ctrl.setValidators([
        Validators.required,
        Validators.maxLength(this.GENERAL_LIMITS.emergencyNumber),
        (c: AbstractControl) => this.phoneValidator(c)
      ]);
    } else {
      ctrl.setValidators([Validators.maxLength(this.GENERAL_LIMITS.emergencyNumber)]);
      ctrl.markAsPristine();
      ctrl.markAsUntouched();
    }

    ctrl.updateValueAndValidity();
  }
  get emergencyNumberCtrl(): AbstractControl {
    return this.companyForm.get('emergencyNumber') as AbstractControl;
  }
  private createSlot(start: Date | null = null, end: Date | null = null): FormGroup {
    return this.fb.group({
      start: [start],
      end:   [end]
    });
  }

  getDaySlots(day: string, emergency = false): FormArray<FormGroup> {
    const key = emergency ? `Emergency${day}Slots` : `${day}Slots`;
    return this.companyForm.get(key) as FormArray<FormGroup>;
  }

  addSlot(day: string, emergency = false): void {
    const arr = this.getDaySlots(day, emergency);
    if (arr.length >= 2) {                       // <--- stop at 2
      return;
    }
    arr.push(this.createSlot());
    if (this.isFormInitialized && !this.formModified) this.checkIfFormModified();
  }

  removeSlot(day: string, index: number, emergency = false): void {
    this.getDaySlots(day, emergency).removeAt(index);
    this.companyForm.markAsDirty();
    if (this.isFormInitialized && !this.formModified) this.checkIfFormModified();
  }

  onSlotStartChange(day: string, index: number, emergency = false): void {
    const arr = this.getDaySlots(day, emergency);
    const slot = arr.at(index) as FormGroup;

    const start = slot.get('start')?.value;
    const end = slot.get('end')?.value;

    // Keep your existing "end must be after start" behavior
    if (start && end && end < start) {
      slot.get('end')?.setValue(null);
    }

    // ✅ Auto-enable visibility when user starts entering times
    this.autoEnableAvailabilityVisibilityIfNeeded(emergency);
    this.checkMondayBulkCopy(day, index, emergency);
  }

  // Disable helpers (per slot)
  getDisabledEndHoursForSlot(day: string, index: number, emergency = false): () => number[] {
    return () => {
      const arr = this.getDaySlots(day, emergency);
      const start = (arr.at(index) as FormGroup).get('start')?.value;
      if (!start) return [];
      const h = start.getHours();
      return Array.from({ length: h }, (_, i) => i);
    };
  }
  getDisabledEndMinutesForSlot(day: string, index: number, emergency = false): (hour: number) => number[] {
    return (hour: number) => {
      const arr = this.getDaySlots(day, emergency);
      const start = (arr.at(index) as FormGroup).get('start')?.value;
      if (!start) return [];
      if (hour !== start.getHours()) return [];
      const m = start.getMinutes();
      return Array.from({ length: m }, (_, i) => i);
    };
  }
  getDisabledEndSecondsForSlot(day: string, index: number, emergency = false): (hour: number, minute: number) => number[] {
    return (hour: number, minute: number) => {
      const arr = this.getDaySlots(day, emergency);
      const start = (arr.at(index) as FormGroup).get('start')?.value;
      if (!start) return [];
      if (hour !== start.getHours() || minute !== start.getMinutes()) return [];
      const s = start.getSeconds();
      return Array.from({ length: s }, (_, i) => i);
    };
  }
  slotsLen(day: string, emergency = false): number {
    const arr = this.getDaySlots(day, emergency);
    return arr ? arr.length : 0;
  }
  hasAnyAvailabilitySlotsFilled(): boolean {
    const days = this.weekDays;
    return days.some(day => {
      const arr = this.getDaySlots(day, false);
      return arr?.controls?.some(c => c.get('start')?.value || c.get('end')?.value);
    });
  }

  hasAnyEmergencyAvailabilitySlotsFilled(): boolean {
    const days = this.emergencyWeekDays;
    return days.some(day => {
      const arr = this.getDaySlots(day, true);
      return arr?.controls?.some(c => c.get('start')?.value || c.get('end')?.value);
    });
  }
  onSlotEndChange(day: string, index: number, emergency = false): void {
    // Just auto-enable if user entered something in end time
    this.autoEnableAvailabilityVisibilityIfNeeded(emergency);
    this.checkMondayBulkCopy(day, index, emergency);
  }
  private autoEnableAvailabilityVisibilityIfNeeded(emergency: boolean): void {
    const controlName = emergency ? 'showEmergencyAvailability' : 'showAvailability';
    const ctrl = this.companyForm.get(controlName);

    if (!ctrl) return;

    // Only auto-enable if it is currently OFF and the user has entered any time
    const hasAnyTime = emergency ? this.hasAnyEmergencyAvailabilitySlotsFilled() : this.hasAnyAvailabilitySlotsFilled();
    if (!ctrl.value && hasAnyTime) {
      ctrl.setValue(true); // enable and let it behave like a user change

      // One-time informational toast
      if (!emergency && !this.availabilityAutoEnabledNotified) {
        this.availabilityAutoEnabledNotified = true;
        this.message.info(this.translate.instant('COMPANY_PAGE.AVAILABILITY_AUTO_ENABLED'));
      }

      if (emergency && !this.emergencyAutoEnabledNotified) {
        this.emergencyAutoEnabledNotified = true;
        this.message.info(this.translate.instant('COMPANY_PAGE.EMERGENCY_AVAILABILITY_AUTO_ENABLED'));
      }
    }
  }
  private checkMondayBulkCopy(day: string, index: number, emergency = false): void {
    if (day !== 'Monday' || index !== 0) return;

    const arr = this.getDaySlots(day, emergency);
    const slot = arr.at(index) as FormGroup;
    const start = slot.get('start')?.value;
    const end = slot.get('end')?.value;

    if (!start || !end) return;

    const key = emergency ? 'emergency' : 'regular';
    if (this.mondayBulkCopyOffered.has(key)) return;
    this.mondayBulkCopyOffered.add(key);

    const modalRef = this.modal.confirm({
      nzTitle: this.translate.instant('COMPANY_PAGE.BULK_COPY_TITLE'),
      nzContent: this.translate.instant('COMPANY_PAGE.BULK_COPY_DESCRIPTION'),
      nzOnOk: () => {
        this.applyBulkMondaySlots(start, end, emergency);
      }
    });
  }

  private applyBulkMondaySlots(start: Date, end: Date, emergency: boolean): void {
    const days = emergency ? this.emergencyWeekDays : this.weekDays;
    for (let i = 1; i < days.length; i++) {
      const targetArr = this.getDaySlots(days[i], emergency);
      const targetSlot = targetArr.at(0) as FormGroup;
      targetSlot.get('start')?.setValue(new Date(start));
      targetSlot.get('end')?.setValue(new Date(end));
    }
    this.companyForm.markAsDirty();
    if (this.isFormInitialized && !this.formModified) this.checkIfFormModified();
  }
  get caretakerTextLen(): number {
    return (this.companyForm?.get('caretakerText')?.value || '').length;
  }
  get publicLandingPageMessageLen(): number {
    return (this.companyForm?.get('publicLandingPageMessage')?.value || '').length;
  }
  get techServiceTextLen(): number {
    return (this.companyForm?.get('technicalServiceText')?.value || '').length;
  }

  onCompanyLogoLoad(event: Event) {
    const img = event.target as HTMLImageElement;
    if (!img?.naturalWidth || !img?.naturalHeight) return;

    const ratio = img.naturalWidth / img.naturalHeight;
    // tweak threshold if you want: 1.6–2.0 are common
    this.isLogoWide = ratio >= 1.8;
  }

  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.onload = () => resolve(String(reader.result));
      reader.readAsDataURL(file);
    });
  }

  private readBlobAsDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Failed to read blob'));
      reader.onload = () => resolve(String(reader.result));
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Extract a palette from an uploaded logo file without CORS issues.
   * Uses node-vibrant to return categorized swatches.
   */
  private async extractPaletteFromLogoFile(file: File): Promise<string[]> {
    const dataUrl = await this.readFileAsDataUrl(file);

    // node-vibrant can read from a URL/dataURL
    const palette = await Vibrant.from(dataUrl).getPalette();

    const ordered = [
      palette.Vibrant,
      palette.DarkVibrant,
      palette.Muted,
      palette.DarkMuted,
      palette.LightVibrant,
      palette.LightMuted
    ]
      .filter(Boolean)
      .map((swatch) => (swatch as any).hex?.toUpperCase())
      .filter(Boolean) as string[];

    // Deduplicate while preserving order
    return Array.from(new Set(ordered));
  }

  private async extractPaletteFromLogoBlob(blob: Blob): Promise<string[]> {
    const dataUrl = await this.readBlobAsDataUrl(blob);
    const palette = await Vibrant.from(dataUrl).getPalette();

    const ordered = [
      palette.Vibrant,
      palette.DarkVibrant,
      palette.Muted,
      palette.DarkMuted,
      palette.LightVibrant,
      palette.LightMuted
    ]
      .filter(Boolean)
      .map((swatch) => (swatch as any).hex?.toUpperCase())
      .filter(Boolean) as string[];

    return Array.from(new Set(ordered));
  }

  /**
   * Picks the best theme color from the extracted palette.
   * We prefer a more "brand-like" color: Vibrant first, then DarkVibrant, then Muted...
   */
  private applyDetectedPalette(paletteHex: string[]): void {
    this.detectedPalette = paletteHex;
    this.recommendedPaletteColor = paletteHex?.[0] ?? null;
  }

  /** User explicitly picks a palette color -> apply as theme color */
  onPaletteColorSelect(color: string): void {
    this.clearThemeColorRequiredError();
    this.themeColorSelectionSource = 'palette';
    this.companyForm.get('themeColor')?.setValue(color, { emitEvent: false });

    this.customThemeColorControl.setValue(color, { emitEvent: false });
    this.selectedThemeColor = color;
    this.setThemeColor(color);

    this.markFormDirty();
  }

  getCustomThemeColorPreview(): string {
    if (this.themeColorSelectionSource === 'custom' && typeof this.selectedThemeColor === 'string') {
      return this.selectedThemeColor;
    }

    const controlColor = this.extractColorPickerValue(this.customThemeColorControl.value);
    if (controlColor) {
      return controlColor;
    }

    if (typeof this.selectedThemeColor === 'string') {
      return this.selectedThemeColor;
    }

    const formColor = this.extractColorPickerValue(this.companyForm.get('themeColor')?.value);
    return formColor || '#ffffff';
  }

  /** Handler for custom color picker changes (ng-zorro nzOnChange event).
   *  The first emission from the color picker is an internal default caused by initialization.
   *  All subsequent emissions come from real user interaction. */
  onCustomColorPickerChange(event: any): void {
    const eventColor = this.extractColorPickerValue(event);
    const fallbackColor = this.extractColorPickerValue(this.customThemeColorControl.value);
    const color = eventColor || fallbackColor;

    if (!this.customColorPickerReady || !this.customColorPickerUserInteracted) {
      return;
    }

    if (!color) {
      return;
    }

    this.companyForm.get('themeColor')?.setValue(color, { emitEvent: false });
    this.themeColorSelectionSource = 'custom';

    this.selectedThemeColor = color;
    this.setThemeColor(color);

    this.clearThemeColorRequiredError();
    this.markFormDirty();
  }

  private extractColorPickerValue(event: any): string | null {
    if (!event) return null;
    if (typeof event === 'string') return this.normalizeHexColor(event);
    if (typeof event === 'object') {
      // NG-ZORRO nzOnChange emits: { color: NzColor, format: string }
      if (event.color && typeof event.color === 'object') {
        if (typeof event.color.toHexString === 'function') return this.normalizeHexColor(event.color.toHexString());
        if (typeof event.color.toHex === 'function') return this.normalizeHexColor(event.color.toHex());
      }
      if (typeof event.color === 'string') return this.normalizeHexColor(event.color);
      // Direct NzColor object, e.g. customThemeColorControl.value
      if (typeof event.toHexString === 'function') return this.normalizeHexColor(event.toHexString());
      if (typeof event.toHex === 'function') return this.normalizeHexColor(event.toHex());
    }
    return null;
  }

  private normalizeHexColor(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    let hex = value.trim();
    if (!hex) return null;
    if (!hex.startsWith('#')) hex = `#${hex}`;
    const match = /^#([0-9a-fA-F]{6})$/.exec(hex);
    if (!match) return null;
    return `#${match[1].toUpperCase()}`;
  }

  /** Marks the user as having intentionally interacted with the custom color picker.
   *  Only after this flag is true will the color picker's nzOnChange actually copy the
   *  selected color into the companyForm and mark the source as 'custom'. */
  onCustomColorPickerUserIntent(): void {
    this.customColorPickerUserInteracted = true;
  }

  /** Reset the custom color picker state so its next initialization emission is ignored.
   *  Called on company load and after each logo upload.
   *  @param preserveColor if set, the custom picker displays this color instead of resetting to null. */
  private resetCustomColorPickerState(preserveColor?: string | null): void {
    this.customColorPickerReady = false;
    this.customColorPickerUserInteracted = false;

    this.customThemeColorControl.setValue(preserveColor ?? null, { emitEvent: false });

    setTimeout(() => {
      this.customColorPickerReady = true;
    });
  }

  /** Clears the required validation error on the themeColor form control
   *  and hides the inline error message. */
  private clearThemeColorRequiredError(): void {
    const control = this.companyForm.get('themeColor');
    if (!control) return;

    const errors = { ...(control.errors || {}) };
    delete errors['required'];

    control.setErrors(Object.keys(errors).length ? errors : null);
    this.showThemeColorRequiredError = false;
  }

  get hasCustomLogo(): boolean {
    return !!(this.uploadedLogoKey || this.company?.logoUrl) && this.logoUrl !== this.defaultLogoUrl;
  }

  get howToReachUsManagerOrderControl(): FormControl {
    return this.companyForm.get('howToReachUsManagerOrder') as FormControl;
  }

  get orderedManagerTypesForUI(): string[] {
    const order: string[] = this.howToReachUsManagerOrderControl.value || [];
    const remaining = this.HOW_TO_REACH_US_MANAGER_TYPES.filter(t => !order.includes(t));
    return [...order, ...remaining];
  }

  isManagerInHowToReachUsOrder(type: string): boolean {
    const order: string[] = this.howToReachUsManagerOrderControl.value || [];
    return order.includes(type);
  }

  toggleManagerInHowToReachUsOrder(type: string): void {
    const order: string[] = [...(this.howToReachUsManagerOrderControl.value || [])];
    const idx = order.indexOf(type);
    if (idx !== -1) {
      order.splice(idx, 1);
    } else {
      order.push(type);
    }
    this.howToReachUsManagerOrderControl.setValue(order);
    this.howToReachUsManagerOrderControl.updateValueAndValidity();
    this.markFormDirty();
  }

  private howToReachUsManagerValidator(group: FormGroup): null {
    const showManager = group.get('showManagerInHowToReachUs')?.value;
    const order: string[] = group.get('howToReachUsManagerOrder')?.value || [];
    const orderControl = group.get('howToReachUsManagerOrder');
    if (showManager && order.length === 0 && orderControl) {
      orderControl.setErrors({ howToReachUsManagerOrderRequired: true });
    } else if (orderControl && orderControl.hasError('howToReachUsManagerOrderRequired')) {
      const errors = { ...orderControl.errors };
      delete errors['howToReachUsManagerOrderRequired'];
      orderControl.setErrors(Object.keys(errors).length > 0 ? errors : null);
    }
    return null;
  }

  onHowToReachUsManagerOrderDrop(event: CdkDragDrop<string[]>): void {
    const order: string[] = [...(this.howToReachUsManagerOrderControl.value || [])];
    const maxIndex = order.length;
    const previousIndex = Math.min(event.previousIndex, maxIndex - 1);
    const currentIndex = Math.min(event.currentIndex, maxIndex);
    if (previousIndex >= 0 && currentIndex <= maxIndex) {
      moveItemInArray(order, previousIndex, currentIndex);
      this.howToReachUsManagerOrderControl.setValue(order);
      this.markFormDirty();
    }
  }

  onShowManagerInHowToReachUsChange(checked: boolean): void {
    if (checked) {
      const order: string[] = this.howToReachUsManagerOrderControl.value || [];
      if (order.length === 0) {
        this.howToReachUsManagerOrderControl.setValue([...this.HOW_TO_REACH_US_MANAGER_TYPES]);
        this.markFormDirty();
      }
    }
    this.howToReachUsManagerOrderControl.updateValueAndValidity();
  }

  getManagerTypeLabelKey(type: string): string {
    const map: Record<string, string> = {
      propertyManager: 'MANAGER_TYPE_PROPERTY',
      technicalAssetsManager: 'MANAGER_TYPE_TECHNICAL_ASSETS',
      sevManager: 'MANAGER_TYPE_SEV',
      accountant: 'MANAGER_TYPE_ACCOUNTANT',
    };
    return map[type] || '';
  }

  isManagerTypeConfigured(type: string): boolean {
    if (!this.templateHeaders) return true;
    const map: Record<string, string[]> = {
      propertyManager: this.templateHeaders.propertyManagerHeaders || [],
      technicalAssetsManager: this.templateHeaders.technicalAssetsManagerHeaders || [],
      sevManager: this.templateHeaders.sevManagerHeaders || [],
      accountant: this.templateHeaders.accountantHeaders || [],
    };
    return (map[type] || []).length > 0;
  }

  private loadTemplateHeaders(): void {
    this.templateService.getSavedHeaders()
      .pipe(timeout(15000))
      .subscribe({
      next: (headers) => {
        this.templateHeaders = headers;
        this.syncManagerConfigWithTemplateHeaders();
      },
      error: () => {
        this.templateHeaders = { ...defaultSavedHeaders };
        this.syncManagerConfigWithTemplateHeaders();
      }
    });
  }

  private syncManagerConfigWithTemplateHeaders(): void {
    const showCheckboxes: Record<string, string> = {
      propertyManager: 'showPropertyManager',
      technicalAssetsManager: 'showTechnicalAssetsManager',
      sevManager: 'showSevManager',
      accountant: 'showAccountant',
    };

    for (const type of this.HOW_TO_REACH_US_MANAGER_TYPES) {
      const controlName = showCheckboxes[type];
      const control = controlName ? this.companyForm.get(controlName) : null;
      if (!control) continue;

      if (!this.templateHeaders) {
        const persistedPreference = this.getPersistedManagerVisibilityPreference(type);
        if (typeof persistedPreference === 'boolean') {
          control.setValue(persistedPreference, { emitEvent: false });
        }
        continue;
      }

      if (!this.isManagerTypeConfigured(type)) {
        control.setValue(false, { emitEvent: false });
        const order: string[] = this.howToReachUsManagerOrderControl.value || [];
        const idx = order.indexOf(type);
        if (idx !== -1) {
          order.splice(idx, 1);
          this.howToReachUsManagerOrderControl.setValue(order, { emitEvent: false });
        }
        continue;
      }

      if (control.dirty || !this.company) {
        continue;
      }

      const persistedPreference = this.getPersistedManagerVisibilityPreference(type);
      control.setValue(
        typeof persistedPreference === 'boolean' ? persistedPreference : true,
        { emitEvent: false }
      );
    }
  }

  private getPersistedManagerVisibilityPreference(type: string): boolean | undefined {
    const companyFields: Record<string, keyof Company> = {
      propertyManager: 'showPropertyManager',
      technicalAssetsManager: 'showTechnicalAssetsManager',
      sevManager: 'showSevManager',
      accountant: 'showAccountant',
    };
    const field = companyFields[type];
    const value = field ? this.company?.[field] : undefined;
    return typeof value === 'boolean' ? value : undefined;
  }
}
